<template>
    <div class="new-note-container">
        <!-- 左侧笔记区域 -->
        <div class="left-panel" ref="leftPanel">
            <!-- 媒体播放区域 -->
            <transition name="media-player">
                <div class="media-player-area" v-show="showMediaPlayer">
                    <div class="media-player-container">
                        <!-- 音频播放器 -->
                        <transition name="fade">
                            <div v-if="['record', 'audio'].includes(noteobj?.filetype)" class="audio-player">
                                <div class="player-backdrop"></div>
                                <div class="player-content">
                                    <div class="player-left">
                                        <div class="play-button-wrapper">
                                            <div class="play-button" @click="toggleAudioPlay"
                                                :class="{ 'playing': isAudioPlaying, 'loading': isAudioLoading }"
                                                :disabled="isAudioLoading">
                                                <div class="button-glow"></div>
                                                <i v-if="isAudioLoading" class="el-icon-loading"></i>
                                                <i v-else
                                                    :class="isAudioPlaying ? 'el-icon-video-pause' : 'el-icon-video-play'"></i>
                                            </div>
                                            <div v-if="isAudioLoading" class="loading-text">
                                                Loading {{ audioLoadingProgress > 0 ? Math.floor(audioLoadingProgress) + '%' : '...'
                                                }}
                                            </div>
                                        </div>
                                    </div>

                                    <div class="player-center">
                                        <div v-if="isAudioLoading" class="loading-progress-bar">
                                            <div class="progress-fill" :style="{ width: audioLoadingProgress + '%' }">
                                            </div>
                                        </div>
                                        <div class="audio-visualizer" v-else-if="isAudioPlaying">
                                            <div class="fill"></div>
                                            <div class="bar" v-for="i in 12" :key="i"></div>
                                            <div class="fill"></div>
                                        </div>
                                        <div class="audio-visualizer-placeholder" v-else>
                                            <div class="placeholder-line"></div>
                                        </div>
                                    </div>

                                    <div class="player-right">
                                        <div class="time-display">
                                            <span class="current-time">{{ formatTime(currentAudioTime) }}</span>
                                        </div>
                                        <div class="audio-control-button" @click="downloadAudio" title="Download">
                                            <i class="el-icon-download"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </transition>
                        <!-- YouTube播放器 -->
                        <transition name="fade">
                            <div v-if="noteobj?.filetype === 'youtube'" class="youtube-player">
                                <YoutubePlayer v-if="noteobj?.filelinkpath" :url="noteobj?.filelinkpath" />
                            </div>

                        </transition>
                    </div>
                </div>
            </transition>

            <!-- 笔记内容区域 -->
            <div class="note-content">
                <!-- 复制功能图标组 -->
                <div class="copy-icons">
                    <el-tooltip v-if="noteobj?.filetype !== 'pdf'" content="Toggle Media Player" placement="top">
                        <i class="el-icon-caret-bottom toggle-media-icon" :class="{ 'rotated': showMediaPlayer }"
                            @click="toggleMediaPlayer"></i>
                    </el-tooltip>
                    <div class="more-actions-container">
                        <el-tooltip content="More Actions" placement="top">
                            <i class="el-icon-more more-icon" @click="toggleMoreActions"></i>
                        </el-tooltip>
                        <transition name="action-menu">
                            <div class="action-menu" v-show="showMoreActions" @click.stop>
                                <div class="action-item" @click="handleCopy('markdown')">
                                    <i class="el-icon-document"></i>
                                    <span>Copy as Markdown</span>
                                </div>
                                <div class="action-item" @click="handleCopy('pdf')">
                                    <i class="el-icon-download"></i>
                                    <span>Download as PDF</span>
                                </div>
                                <div class="action-item" @click="handleShare">
                                    <i class="el-icon-share"></i>
                                    <span>Share Note</span>
                                </div>
                                <div class="action-item delete-item" @click="handleDelete">
                                    <i class="el-icon-delete"></i>
                                    <span>Delete Note</span>
                                </div>
                            </div>
                        </transition>
                    </div>
                </div>

                <!-- 骨架屏加载效果 -->
                <div class="note-skeleton" v-if="loading">
                    <!-- 添加进度条 -->
                    <div class="note-loading-progress">
                        <div class="progress-track">
                            <div class="progress-fill" :style="{ width: loadingProgress + '%' }"></div>
                        </div>
                        <div class="progress-text">{{ Math.floor(loadingProgress) }}%</div>
                    </div>
                    <div class="skeleton-header">
                        <div class="skeleton-title"></div>
                        <div class="skeleton-subtitle"></div>
                    </div>
                    <div class="skeleton-content">
                        <div class="skeleton-paragraph" v-for="i in 3" :key="i"></div>
                        <div class="skeleton-heading"></div>
                        <div class="skeleton-paragraph" v-for="i in 5" :key="i + 3"></div>
                        <div class="skeleton-code-block"></div>
                        <div class="skeleton-paragraph" v-for="i in 4" :key="i + 8"></div>
                        <div class="skeleton-heading"></div>
                        <div class="skeleton-paragraph" v-for="i in 3" :key="i + 12"></div>
                        <div class="skeleton-list">
                            <div class="skeleton-list-item" v-for="i in 3" :key="i + 15"></div>
                        </div>
                        <div class="skeleton-paragraph" v-for="i in 2" :key="i + 18"></div>
                    </div>
                </div>

                <!-- 失败状态显示 -->
                <div class="note-failed-container" v-else-if="noteobj?.status === 3">
                    <div class="failed-content">
                        <h2 class="failed-title">Failed to Generate Note</h2>
                        <p class="failed-message">We encountered some issues while processing your file and couldn't
                            generate the note content.</p>
                        <div class="failed-actions">
                            <button v-if="noteobj?.task_count <= 1" class="retry-button" @click="retryNoteGeneration"
                                :disabled="isRetrying">
                                <span v-if="!isRetrying">Try Again</span>
                                <i v-else class="el-icon-loading"></i>
                            </button>
                            <div v-else class="retry-limit-message">
                                Retry limit exceeded. We recommend creating a new note with a different file.
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 笔记渲染区域 -->
                <div class="note-render-area" ref="noteContent" v-else>
                    <template v-if="noteobj?.filetype === 'record'">
                        <div class="textbox" v-for="(textobj, i) in noteobj?.live_transcript?.part_objs" :key="i">
                            <h5>{{ textobj.time }}</h5>
                            <div class="yuantext markdown-content" v-html="textobj.mk_notes"></div>
                        </div>
                    </template>
                    <template v-else>
                        <div class="markdown-content" v-html="renderedNotes"></div>
                        <div v-if="isLargeNote && virtualScrolling" class="virtual-scroll-info">
                            <div class="scroll-info-badge">
                                <i class="el-icon-info"></i>
                                <span>Virtual Scroll Enabled (Large Note Optimization)</span>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </div>

        <!-- 拖动条 -->
        <div class="resizer" ref="resizer"></div>

        <!-- 右侧功能区域 -->
        <div class="right-panel" ref="rightPanel">
            <!-- 功能切换标签 -->
            <div class="feature-tabs" ref="featureTabs">
                <div class="tabs-container" ref="tabsContainer">
                    <template v-for="(tab, index) in filteredTabs">
                        <el-tooltip :key="tab.key"
                            :content="tab.label"
                            placement="top"
                            :disabled="!isCompactMode"
                            :enterable="false"
                            :open-delay="300">
                            <div class="tab-item" 
                                :class="{ active: currentTab === tab.key, disabled: noteobj?.status === 3, 'icon-only': isCompactMode }"
                                @click="noteobj?.status !== 3 && switchTab(tab, index)">
                                <span class="tab-icon" v-html="tab.icon"></span>
                                <span class="tab-label" v-show="!isCompactMode">{{ tab.label }}</span>
                            </div>
                        </el-tooltip>
                    </template>
                    <div class="slider" :style="sliderStyle"></div>
                </div>
                <div class="tabs-fade-overlay"></div>
            </div>

            <!-- 功能组件渲染区域 -->
            <div class="feature-content">
                <!-- 当笔记生成失败时显示禁用提示 -->
                <div v-if="noteobj?.status === 3" class="feature-disabled-container">
                    <div class="disabled-content">
                        <i class="el-icon-warning-outline"></i>
                        <h3>Features Not Available</h3>
                        <p>The note generation failed, so these features cannot be used. Please try regenerating the
                            note first.
                        </p>
                    </div>
                </div>

                <!-- 正常功能组件 -->
                <template v-else>
                    <notechatbot v-if="currentTab === 'chatbot'" :noteobj="noteobj" />
                    <div class="transcript-container" v-if="currentTab === 'transcript'">
                        <!-- 添加复制按钮 -->
                        <div class="transcript-tools">
                            <el-tooltip content="Copy Transcript" placement="top">
                                <div class="transcript-copy-btn" @click="copyTranscript">
                                    <i class="el-icon-document-copy"></i>
                                </div>
                            </el-tooltip>
                        </div>
                        <notetranscript :transcript="transcript" />
                    </div>
                    <TranslateToggle v-if="currentTab === 'translate'" :noteobj="noteobj" @setnoteobj="updateNoteObj" />
                    <new_note_mindmap v-if="currentTab === 'mindmap'" :noteobj="noteobj" class="feature-component" />
                    <new_note_flashcards v-if="currentTab === 'flashcards'" :noteobj="noteobj"
                        @setflashcards="setflashcards" class="feature-component"
                        :generation-state="generationStates.flashcards" />
                    <new_note_quiz v-if="currentTab === 'quiz'" :noteobj="noteobj" @setquiz="setquiz"
                        class="feature-component"
                        :generation-state="generationStates.quiz" />
                </template>
            </div>
        </div>

        <!-- 添加确认删除对话框 -->
        <el-dialog title="Confirm Delete" :visible.sync="deleteDialogVisible" width="400px"
            custom-class="delete-confirm-dialog" :show-close="false" center>
            <div class="delete-dialog-content">
                <i class="el-icon-warning-outline warning-icon"></i>
                <div class="delete-message">
                    <p class="delete-title">Are you sure you want to delete this note?</p>
                    <p class="delete-desc">This action cannot be undone. Please confirm.</p>
                </div>
            </div>
            <span slot="footer" class="dialog-footer">
                <el-button @click="cancelDelete">Cancel</el-button>
                <el-button type="danger" @click="confirmDelete">Delete</el-button>
            </span>
        </el-dialog>

        <!-- 添加自定义分享弹窗 -->
        <transition name="share-fade">
            <div class="custom-share-dialog-overlay" v-if="shareDialogVisible" @click="closeShareDialog">
                <div class="custom-share-dialog" @click.stop>
                    <div class="share-dialog-header">
                        <button class="close-button" @click="closeShareDialog">
                            <i class="el-icon-close"></i>
                        </button>
                    </div>
                    <div class="share-dialog-content">
                        <h3>Share Your Note</h3>
                        <p>The shareable link has been copied to clipboard. Anyone can view the note through this link.
                        </p>

                        <div class="share-link-container">
                            <input type="text" ref="shareInput" v-model="shareUrl" readonly class="share-input" />
                            <button class="copy-button" @click="copyShareLink">
                                <i class="el-icon-document-copy"></i>
                                Copy
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <!-- 添加中央消息提示 -->
        <transition name="center-message-fade">
            <div v-if="centerMessageVisible" class="center-message" :class="centerMessageType">
                <i :class="centerMessageType === 'success' ? 'el-icon-success' : 'el-icon-error'"></i>
                <span>{{ centerMessageText }}</span>
            </div>
        </transition>
    </div>
</template>

<script>
import { renderMarkdown } from '@/utils/markdown';
import notechatbot from './new_note_chatbot.vue';
import notetranscript from './new_note_transcript.vue';
import TranslateToggle from './new_translate_toggle.vue';
import new_note_mindmap from './new_note_mindmap.vue';
import new_note_flashcards from './new_note_flashcards.vue';
import new_note_quiz from './new_note_quiz.vue';
import YoutubePlayer from '@/components/YoutubePlayer.vue'
export default {
    name: 'NewNotePage',
    components: {
        notechatbot,
        notetranscript,
        TranslateToggle,
        new_note_mindmap,
        new_note_flashcards,
        new_note_quiz,
        YoutubePlayer
    },
    props: {
        noteobj: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            loading: true,
            currentTab: 'mindmap',
            showMediaPlayer: true,
            isAudioPlaying: false,
            showMoreActions: false,
            deleteDialogVisible: false, // 添加删除确认对话框显示状态
            shareDialogVisible: false, // 添加分享对话框显示状态
            shareUrl: '', // 分享链接
            centerMessageVisible: false, // 添加中央消息显示状态
            centerMessageText: '', // 中央消息文本
            centerMessageType: 'success', // 中央消息类型
            noteAreaHeight: 'calc(100vh - 150px)', // 默认高度
            audioElement: null, // 音频元素
            allTabs: [
                {
                    key: 'mindmap',
                    label: 'MindMap',
                    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="6" cy="12" r="3"></circle>
                        <circle cx="19" cy="5" r="1.8"></circle>
                        <circle cx="19" cy="12" r="1.8"></circle>
                        <circle cx="19" cy="19" r="1.8"></circle>
                        <line x1="9" y1="12" x2="17.2" y2="12"></line>
                        <polyline points="8.5,9.5 14,9.5 14,5 17.2,5"></polyline>
                        <polyline points="8.5,14.5 14,14.5 14,19 17.2,19"></polyline>
                    </svg>`
                },
                {
                    key: 'chatbot',
                    label: 'Chat',
                    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                        <circle cx="9" cy="10" r="1"></circle>
                        <circle cx="12" cy="10" r="1"></circle>
                        <circle cx="15" cy="10" r="1"></circle>
                    </svg>`
                },
                {
                    key: 'translate',
                    label: 'Translate',
                    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M5 8l6 6"></path>
                        <path d="M4 14h7"></path>
                        <path d="M2 5h12"></path>
                        <path d="M7 2h1"></path>
                        <path d="M22 22l-5-10-5 10"></path>
                        <path d="M14 18h6"></path>
                    </svg>`
                },
                {
                    key: 'flashcards',
                    label: 'Flashcards',
                    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="4" y="3" width="10" height="15" rx="1" ry="1"></rect>
                        <path d="M8.5 8L8.5 14.5" stroke-width="1.5"></path>
                        <rect x="8" y="6" width="10" height="15" rx="1" ry="1" transform="rotate(8, 13, 13)" stroke-opacity="0.7"></rect>
                    </svg>`
                },
                {
                    key: 'quiz',
                    label: 'Quiz',
                    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M9 11l3 3L22 4"></path>
                        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                    </svg>`
                },
                {
                    key: 'transcript',
                    label: 'Transcript',
                    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                        <polyline points="10 9 9 9 8 9"></polyline>
                    </svg>`
                },
            ],
            currentTabIndex: 0,
            transcript: {},

            sliderPosition: 0,
            sliderWidth: 0,
            savedRightPanelWidth: '0px', // 保存右侧面板宽度
            currentAudioTime: 0,
            audioProgressPercentage: 0,
            isProcessingAudio: false, // 添加处理音频状态变量
            isAudioLoading: false, // 音频加载状态
            autoPlayRetries: 0, // 自动重试播放计数
            maxAutoPlayRetries: 3, // 最大自动重试次数
            audioLoadingProgress: 0, // 音频加载进度百分比
            playbackMonitor: null, // 播放监控
            isRetrying: false,
            loadingProgress: 0, // 笔记加载进度
            loadingProgressInterval: null, // 进度条定时器
            sidebarObserver: null, // 侧边栏变化观察者
            resizeObserver: null, // 添加ResizeObserver
            lastSidebarWidth: 0, // 记录上一次侧边栏宽度
            isAdjusting: false, // 标记是否正在调整中
            adjustTimeout: null, // 调整的timeout ID
            
            // 添加虚拟滚动相关状态
            isLargeNote: false,
            virtualScrolling: false,
            visibleChunks: [],
            chunkSize: 5000, // 每个块的字符数
            noteChunks: [],
            scrollPosition: 0,
            scrollThreshold: 100, // 滚动阈值，用于提前加载
            isInitialRender: true,
            renderTimeout: null,
            intersectionObserver: null,
            renderedContent: '',
            
            // 添加标签紧凑模式
            isCompactMode: false,
            compactModeThreshold: 510, // 紧凑模式的宽度阈值
            
            // 添加生成状态管理
            generationStates: {
                flashcards: {
                    isGenerating: false,
                    progress: 0,
                    startTime: null,
                    pollTimer: null,
                    maxTries: 100,
                    currentTry: 0
                },
                quiz: {
                    isGenerating: false,
                    progress: 0,
                    startTime: null,
                    pollTimer: null,
                    maxTries: 100,
                    currentTry: 0
                }
            },
        };
    },
    computed: {
        // 修改renderedNotes计算属性，支持虚拟滚动
        renderedNotes() {
            if (!this.noteobj?.note_text) return '';
            
            // 如果不需要虚拟滚动，直接渲染全部内容
            if (!this.virtualScrolling) {
                return renderMarkdown(this.noteobj.note_text);
            }
            
            // 使用已经渲染好的内容
            return this.renderedContent;
        },
        sliderStyle() {
            return {
                transform: `translateX(${this.sliderPosition}px)`,
                width: `${this.sliderWidth}px`
            };
        },
        // 添加计算属性，根据笔记类型过滤标签
        filteredTabs() {
            if (this.noteobj?.filetype === 'record') {
                // 当笔记类型是record时，过滤掉chatbot标签
                return this.allTabs.filter(tab => tab.key !== 'chatbot');
            }
            return this.allTabs;
        }
    },
    watch: {
        noteobj: {
            handler(newVal) {
                if (newVal?.filetype === 'record') {
                    // 如果当前标签是chatbot，切换到mindmap
                    if (this.currentTab === 'chatbot') {
                        this.currentTab = 'mindmap';
                        this.currentTabIndex = 0;
                        this.$nextTick(() => {
                            this.updateSliderPosition();
                        });
                    }
                    
                    this.transcript["value"] = this.noteobj?.live_transcript;
                    this.transcript["notetoken"] = this.noteobj?.notetoken;
                    this.transcript["type"] = this.noteobj?.filetype;

                    this.transcript.value?.part_objs.forEach((item) => {
                        item['mk_notes'] = renderMarkdown(item.notes);
                    });
                } else {
                    this.transcript = {
                        value: newVal?.note_transcript,
                        notetoken: newVal?.notetoken,
                        type: newVal?.filetype
                    };
                    
                    // 判断笔记大小，决定是否使用虚拟滚动
                    if (newVal?.note_text && newVal.note_text.length > 30000) {
                        this.isLargeNote = true;
                        this.prepareVirtualScrolling(newVal.note_text);
                    } else {
                        this.isLargeNote = false;
                        this.virtualScrolling = false;
                        this.renderedContent = '';
                    }
                }
                
                if (newVal?.status == 1) {
                    this.loading = false;
                    // 笔记加载完成，进度条直接到100%
                    this.loadingProgress = 100;
                    this.clearLoadingProgressInterval();
                } else if (newVal?.status == 3) {
                    this.loading = false;
                    // 笔记加载失败，清除进度条
                    this.clearLoadingProgressInterval();
                } else if (newVal?.status == 0) {
                    this.loading = true;
                    // 开始加载笔记，初始化并启动进度条
                    this.initLoadingProgress();
                }

                // 如果是媒体类型的笔记，更新播放器高度
                if (newVal && ['record', 'audio', 'youtube'].includes(newVal.filetype)) {
                    this.$nextTick(() => {
                        setTimeout(() => {
                            this.updateNoteAreaHeight();
                        }, 300);
                    });
                }
            },
            deep: true,
            immediate: true
        },
        showMediaPlayer: {
            handler() {
                // 根据媒体播放器的显示状态动态计算笔记区域的高度
                this.$nextTick(() => {
                    setTimeout(() => {
                        this.updateNoteAreaHeight();
                    }, 300);
                });
            }
        },
        // 添加一个监听器，监听父组件传递的侧边栏折叠状态变化
        '$parent.isCollapsed': {
            handler() {
                // 当侧边栏状态变化时，延迟一点时间后重新调整面板宽度
                this.$nextTick(() => {
                    setTimeout(() => {
                        this.adjustPanelWidths();
                        this.checkTabsOverflow();
                        this.updateSliderPosition();
                    }, 300); // 给过渡动画一些时间
                });
            },
            deep: true
        }
    },
    mounted() {
        this.initResizer();
        this.$nextTick(() => {
            // 如果是录音笔记且当前标签是聊天，则切换到思维导图标签
            if (this.noteobj?.filetype === 'record' && this.currentTab === 'chatbot') {
                this.currentTab = 'mindmap';
                this.currentTabIndex = 0; // mindmap通常是第一个标签
            }
            
            this.checkTabsOverflow();
            this.checkTabsCompactMode(); // 初始检查紧凑模式
            // 添加窗口大小变化事件监听
            window.addEventListener('resize', this.handleWindowResize);
            // 使用ResizeObserver替代MutationObserver来监听侧边栏尺寸变化
            this.setupResizeObserver();
            this.updateSliderPosition();
            this.updateNoteAreaHeight(); // 初始化笔记区域高度
            
            // 设置滚动监听
            this.setupScrollObserver();
        });

        // 如果页面加载时就处于loading状态，初始化进度条
        console.log('newnotepage-noteobj',this.noteobj);
        if (this.loading) {
            this.initLoadingProgress();
        }

        // 添加点击事件监听，用于关闭菜单
        document.addEventListener('click', this.handleClickOutside);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.checkTabsOverflow);
        // 移除窗口大小变化事件监听
        window.removeEventListener('resize', this.handleWindowResize);
        this.cleanupAudioPlayer();
        this.cleanupPlaybackMonitoring(); // 清理播放监控
        this.clearLoadingProgressInterval(); // 清理进度条定时器
        
        // 清理观察者
        if (this.sidebarObserver) {
            this.sidebarObserver.disconnect();
        }
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        // 清理timeout
        if (this.adjustTimeout) {
            clearTimeout(this.adjustTimeout);
        }
        if (this.renderTimeout) {
            clearTimeout(this.renderTimeout);
        }
        // 清理滚动观察者
        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
        }

        // 移除点击事件监听器
        document.removeEventListener('click', this.handleClickOutside);
    },
    methods: {
        // 准备虚拟滚动
        prepareVirtualScrolling(noteText) {
            // 如果笔记文本为空，直接返回
            if (!noteText) {
                this.virtualScrolling = false;
                return;
            }
            
            // 将笔记文本分成多个块
            this.noteChunks = this.chunkifyMarkdown(noteText);
            
            // 如果分块后数量少于3个，不使用虚拟滚动
            if (this.noteChunks.length <= 3) {
                this.virtualScrolling = false;
                this.renderedContent = '';
                return;
            }
            
            // 启用虚拟滚动
            this.virtualScrolling = true;
            this.visibleChunks = [0, 1, 2]; // 初始可见的块索引
            
            // 初始渲染前三个块
            this.$nextTick(() => {
                this.renderVisibleChunks();
            });
        },
        
        // 将Markdown文本分块，保持Markdown结构完整性
        chunkifyMarkdown(text) {
            const chunks = [];
            let currentChunk = '';
            let currentSize = 0;
            
            // 按行分割文本
            const lines = text.split('\n');
            
            for (let i = 0; i < lines.length; i++) {
                const line = lines[i];
                const lineLength = line.length + 1; // +1 for the newline character
                
                // 如果当前行是标题或当前块已经足够大
                if ((line.startsWith('#') && currentSize >= this.chunkSize) || 
                    currentSize + lineLength > this.chunkSize * 1.5) {
                    
                    // 保存当前块并开始新块
                    if (currentChunk) {
                        chunks.push(currentChunk);
                    }
                    currentChunk = line + '\n';
                    currentSize = lineLength;
                } else {
                    // 继续添加到当前块
                    currentChunk += line + '\n';
                    currentSize += lineLength;
                }
            }
            
            // 添加最后一个块
            if (currentChunk) {
                chunks.push(currentChunk);
            }
            
            return chunks;
        },
        
        // 渲染可见的块
        renderVisibleChunks() {
            if (!this.virtualScrolling || this.noteChunks.length === 0) return;
            
            // 获取可见块的内容并渲染
            const visibleContent = this.visibleChunks
                .filter(index => index >= 0 && index < this.noteChunks.length)
                .map(index => this.noteChunks[index])
                .join('\n');
            
            // 渲染内容
            this.renderedContent = renderMarkdown(visibleContent);
            
            // 如果是初次渲染，添加占位符
            if (this.isInitialRender) {
                this.$nextTick(() => {
                    this.addScrollPlaceholders();
                    this.isInitialRender = false;
                });
            }
        },
        
        // 添加滚动占位符
        addScrollPlaceholders() {
            const noteContent = this.$refs.noteContent;
            if (!noteContent || !this.virtualScrolling) return;
            
            // 创建顶部和底部占位符
            const topPlaceholder = document.createElement('div');
            topPlaceholder.className = 'scroll-placeholder top-placeholder';
            topPlaceholder.id = 'top-placeholder';
            topPlaceholder.style.height = '10px';
            
            const bottomPlaceholder = document.createElement('div');
            bottomPlaceholder.className = 'scroll-placeholder bottom-placeholder';
            bottomPlaceholder.id = 'bottom-placeholder';
            bottomPlaceholder.style.height = '10px';
            
            // 插入占位符
            const contentElement = noteContent.querySelector('.markdown-content');
            if (contentElement) {
                contentElement.insertBefore(topPlaceholder, contentElement.firstChild);
                contentElement.appendChild(bottomPlaceholder);
            }
        },
        
        // 设置滚动观察者
        setupScrollObserver() {
            // 如果浏览器支持IntersectionObserver
            if ('IntersectionObserver' in window) {
                // 创建观察者
                this.intersectionObserver = new IntersectionObserver(
                    (entries) => {
                        entries.forEach(entry => {
                            if (entry.isIntersecting) {
                                // 根据占位符ID决定加载方向
                                if (entry.target.id === 'top-placeholder') {
                                    this.loadMoreChunksAbove();
                                } else if (entry.target.id === 'bottom-placeholder') {
                                    this.loadMoreChunksBelow();
                                }
                            }
                        });
                    },
                    {
                        root: this.$refs.noteContent,
                        rootMargin: '200px 0px',
                        threshold: 0.1
                    }
                );
                
                // 在DOM更新后观察占位符
                this.$nextTick(() => {
                    const topPlaceholder = document.getElementById('top-placeholder');
                    const bottomPlaceholder = document.getElementById('bottom-placeholder');
                    
                    if (topPlaceholder) {
                        this.intersectionObserver.observe(topPlaceholder);
                    }
                    if (bottomPlaceholder) {
                        this.intersectionObserver.observe(bottomPlaceholder);
                    }
                });
            }
            
            // 添加滚动事件监听
            if (this.$refs.noteContent) {
                this.$refs.noteContent.addEventListener('scroll', this.handleScroll);
            }
        },
        
        // 处理滚动事件
        handleScroll(event) {
            if (!this.virtualScrolling) return;
            
            const { scrollTop, scrollHeight, clientHeight } = event.target;
            this.scrollPosition = scrollTop;
            
            // 检查是否接近顶部或底部
            const isNearTop = scrollTop < this.scrollThreshold;
            const isNearBottom = scrollHeight - scrollTop - clientHeight < this.scrollThreshold;
            
            if (isNearTop) {
                this.loadMoreChunksAbove();
            }
            
            if (isNearBottom) {
                this.loadMoreChunksBelow();
            }
        },
        
        // 加载上方的更多块
        loadMoreChunksAbove() {
            if (!this.virtualScrolling || this.visibleChunks.length === 0) return;
            
            const firstVisibleIndex = Math.min(...this.visibleChunks);
            if (firstVisibleIndex <= 0) return; // 已经是第一块
            
            // 添加上方的块
            const newVisibleChunks = [...this.visibleChunks];
            newVisibleChunks.unshift(firstVisibleIndex - 1);
            
            // 如果块太多，移除底部块
            if (newVisibleChunks.length > 5) {
                newVisibleChunks.pop();
            }
            
            this.visibleChunks = newVisibleChunks;
            
            // 防抖渲染
            if (this.renderTimeout) {
                clearTimeout(this.renderTimeout);
            }
            this.renderTimeout = setTimeout(() => {
                this.renderVisibleChunks();
            }, 100);
        },
        
        // 加载下方的更多块
        loadMoreChunksBelow() {
            if (!this.virtualScrolling || this.visibleChunks.length === 0) return;
            
            const lastVisibleIndex = Math.max(...this.visibleChunks);
            if (lastVisibleIndex >= this.noteChunks.length - 1) return; // 已经是最后一块
            
            // 添加下方的块
            const newVisibleChunks = [...this.visibleChunks];
            newVisibleChunks.push(lastVisibleIndex + 1);
            
            // 如果块太多，移除顶部块
            if (newVisibleChunks.length > 5) {
                newVisibleChunks.shift();
            }
            
            this.visibleChunks = newVisibleChunks;
            
            // 防抖渲染
            if (this.renderTimeout) {
                clearTimeout(this.renderTimeout);
            }
            this.renderTimeout = setTimeout(() => {
                this.renderVisibleChunks();
            }, 100);
        },
        
        toggleMediaPlayer() {
            this.showMediaPlayer = !this.showMediaPlayer;
        },

        // 切换更多操作菜单
        toggleMoreActions(event) {
            // 阻止事件冒泡，防止触发外部点击事件
            if (event) {
                event.stopPropagation();
            }
            this.showMoreActions = !this.showMoreActions;
        },

        // 音频播放控制
        toggleAudioPlay() {
            this.isAudioLoading = true;
            if (!this.noteobj?.filelinkpath) {
                this.$message.warning('No audio source available');
                this.isAudioLoading = false;
                return;
            }

            // 检查音频元素和音频源是否可用
            if (!this.audioElement || !this.noteobj?.filelinkpath) {
                if (['record', 'audio'].includes(this.noteobj?.filetype)) {
                    this.initAudioPlayer(); // 初始化音频播放器
                }
            }

            if (this.isAudioPlaying) {
                this.audioElement.pause();
                this.isAudioPlaying = false;
                this.isAudioLoading = false;
            } else {
                this.audioElement.play();
                this.isAudioPlaying = true;
                this.isAudioLoading = false;
            }
        },

        // 封装播放音频的方法
        playAudio() {
            // 简化的播放方法，等待重新实现
            console.log('Play audio requested');
        },

        // 实际开始播放的方法
        startPlayback() {
            // 简化的播放开始方法，等待重新实现
            console.log('Start playback requested');
        },

        // 激活音频上下文
        activateAudioContext() {
            // 简化的音频上下文激活方法，等待重新实现
            console.log('Audio context activation requested');
        },

        // 设置播放监控
        setupPlaybackMonitoring() {
            // 简化的播放监控方法，等待重新实现
            console.log('Playback monitoring setup requested');
        },

        // 清理播放监控
        cleanupPlaybackMonitoring() {
            // 清理播放监控
            if (this.playbackMonitor) {
                clearInterval(this.playbackMonitor);
                this.playbackMonitor = null;
            }
        },

        // 添加自动重试播放功能
        retryPlayAudio() {
            // 简化的播放重试方法，等待重新实现
            console.log('Retry play audio requested');
        },

        // 添加专门的恢复播放方法
        resumePlaybackAfterPause() {
            // 简化的恢复播放方法，等待重新实现
            console.log('Resume playback requested');
        },

        // 初始化音频播放器
        initAudioPlayer() {
            // 先清理旧的音频元素
            this.cleanupAudioPlayer();
            // 重置状态
            this.isAudioLoading = false;
            this.audioLoadingProgress = 0;
            this.autoPlayRetries = 0;
            // 创建新的音频元素
            this.audioElement = new Audio();
            // 设置音频预加载
            this.audioElement.preload = 'auto';
            // 明确禁用自动播放
            this.audioElement.autoplay = false;
            // 设置音频源
            if (this.noteobj?.filelinkpath) {
                this.audioElement.src = this.noteobj.filelinkpath;
                this.audioElement.load();
            }
            // 添加基本事件监听
            this.audioElement.addEventListener('timeupdate', this.handleAudioTimeUpdate);
            this.audioElement.addEventListener('ended', this.handleAudioEnded);
            this.audioElement.addEventListener('error', this.handleAudioError);
        },
        // 清理音频播放器
        cleanupAudioPlayer() {
            if (this.audioElement) {
                // 停止播放音频
                this.audioElement.pause();
                this.isAudioPlaying = false;

                // 移除所有事件监听
                this.audioElement.removeEventListener('timeupdate', this.handleAudioTimeUpdate);
                this.audioElement.removeEventListener('ended', this.handleAudioEnded);
                this.audioElement.removeEventListener('error', this.handleAudioError);

                // 释放资源
                this.audioElement.src = '';
                this.audioElement = null;
            }
        },

        // 音频事件处理函数
        handleAudioTimeUpdate() {
            if (this.audioElement && !isNaN(this.audioElement.currentTime) && isFinite(this.audioElement.currentTime)) {
                this.currentAudioTime = this.audioElement.currentTime;
            }
        },

        formatTime(seconds) {
            if (!isFinite(seconds) || isNaN(seconds)) {
                return '0:00';
            }
            const mins = Math.floor(seconds / 60);
            const secs = Math.floor(seconds % 60);
            return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
        },
        async downloadAudio() {
            if (!this.noteobj?.filelinkpath) {
                this.$message.warning('No audio source available');
                return;
            }

            try {
                this.$message.info('Starting download...');

                // 获取文件名
                const filename = this.noteobj.filename || 'audio_recording.mp3';

                // 如果是直接可访问的URL
                if (this.noteobj.filelinkpath.startsWith('http')) {
                    // 使用fetch获取文件内容
                    const response = await fetch(this.noteobj.filelinkpath);
                    if (!response.ok) {
                        throw new Error('Download failed');
                    }

                    // 获取文件blob
                    const blob = await response.blob();

                    // 创建blob URL
                    const blobUrl = window.URL.createObjectURL(blob);

                    // 创建下载链接
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = filename;
                    link.style.display = 'none';

                    // 添加到文档并触发点击
                    document.body.appendChild(link);
                    link.click();

                    // 清理
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(blobUrl);

                    this.$message.success('Download completed!');
                } else {
                    // 如果需要通过API获取下载链接
                    const fileKey = this.noteobj.filepath;

                    // 获取下载链接
                    const { data } = await this.$axios.post('/api/get_download_url/', {
                        noteid: this.noteobj.noteid,
                        fileKey: fileKey
                    });

                    if (!data.downloadUrl) {
                        throw new Error('Failed to get download URL');
                    }

                    // 使用fetch获取文件内容
                    const response = await fetch(data.downloadUrl);
                    if (!response.ok) {
                        throw new Error('Download failed');
                    }

                    // 获取文件blob
                    const blob = await response.blob();

                    // 创建blob URL
                    const blobUrl = window.URL.createObjectURL(blob);

                    // 创建下载链接
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = filename;
                    link.style.display = 'none';

                    // 添加到文档并触发点击
                    document.body.appendChild(link);
                    link.click();

                    // 清理
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(blobUrl);

                    this.$message.success('Download completed!');
                }
            } catch (error) {
                console.error('Download failed:', error);
                this.$message.error(`Download failed: ${error.message}`);
            }
        },
        setflashcards(flashcards) {
            this.$emit('setflashcards', flashcards);
        },
        setquiz(quiz) {
            this.$emit('setquiz', quiz);
        },
        switchTab(tab, index) {
            this.currentTab = tab.key;
            this.currentTabIndex = index;
            this.$nextTick(() => {
                this.updateSliderPosition();
            });
        },
        updateSliderPosition() {
            const tabsContainer = this.$refs.tabsContainer;
            if (!tabsContainer) return;

            const tabElements = tabsContainer.querySelectorAll('.tab-item');
            if (tabElements.length === 0 || !tabElements[this.currentTabIndex]) return;

            const activeTab = tabElements[this.currentTabIndex];
            this.sliderPosition = activeTab.offsetLeft;
            this.sliderWidth = activeTab.offsetWidth;

            // 在紧凑模式下调整滑块宽度
            if (this.isCompactMode) {
                // 紧凑模式下，滑块宽度稍微缩小一点，更为精致
                this.sliderWidth = Math.min(30, this.sliderWidth);
                // 居中滑块
                this.sliderPosition = activeTab.offsetLeft + (activeTab.offsetWidth - this.sliderWidth) / 2;
            }

            // 在窗口大小改变时重新计算slider位置和宽度
            this.$nextTick(() => {
                const recalculatedActiveTab = tabElements[this.currentTabIndex];
                if (recalculatedActiveTab) {
                    this.sliderPosition = recalculatedActiveTab.offsetLeft;
                    this.sliderWidth = recalculatedActiveTab.offsetWidth;
                    
                    // 在紧凑模式下再次调整
                    if (this.isCompactMode) {
                        this.sliderWidth = Math.min(30, this.sliderWidth);
                        this.sliderPosition = recalculatedActiveTab.offsetLeft + 
                            (recalculatedActiveTab.offsetWidth - this.sliderWidth) / 2;
                    }
                }
            });
        },
        closeRightPanel() {
            // 隐藏右侧面板
            const leftPanel = this.$refs.leftPanel;
            const rightPanel = this.$refs.rightPanel;

            // 保存当前状态以便恢复
            this.savedRightPanelWidth = rightPanel.style.width;

            // 隐藏右侧面板和分隔条
            rightPanel.style.display = 'none';
            this.$refs.resizer.style.display = 'none';

            // 将左侧面板宽度设为100%
            leftPanel.style.width = '100%';

            // 发送事件通知父组件右侧面板已关闭
            this.$emit('right-panel-closed');
        },
        openRightPanel() {
            // 重新显示右侧面板
            const leftPanel = this.$refs.leftPanel;
            const rightPanel = this.$refs.rightPanel;
            const resizer = this.$refs.resizer;
            const container = this.$el;

            // 恢复右侧面板和分隔条的显示
            rightPanel.style.display = 'flex';
            resizer.style.display = 'block';

            // 如果有保存的宽度，则恢复
            if (this.savedRightPanelWidth && this.savedRightPanelWidth !== '0px') {
                rightPanel.style.width = this.savedRightPanelWidth;

                // 计算左侧面板宽度
                const containerWidth = container.clientWidth;
                const rightWidth = parseInt(this.savedRightPanelWidth);
                leftPanel.style.width = `${containerWidth - rightWidth - 4}px`; // 4px为分隔条宽度
            } else {
                // 如果没有保存的宽度，则使用默认比例
                const containerWidth = container.clientWidth;
                const leftWidth = (containerWidth * 5.5) / 10;
                const rightWidth = (containerWidth * 4.5) / 10;

                leftPanel.style.width = `${leftWidth}px`;
                rightPanel.style.width = `${rightWidth}px`;
            }

            // 发送事件通知父组件右侧面板已打开
            this.$emit('right-panel-opened');
        },
        switchRightTab(tab) {
            this.currentTab = tab;
            switch (tab) {
                case 'transcript':
                    if (this.noteobj.filetype == 'record') {
                        this.transcript["value"] = this.noteobj?.live_transcript;
                        this.transcript["notetoken"] = this.noteobj?.notetoken;
                    } else {
                        this.transcript["value"] = this.noteobj?.note_transcript;
                        this.transcript["notetoken"] = this.noteobj?.notetoken;
                    }
                    this.transcript["type"] = this.noteobj?.filetype;
                    break;
            }
        },
        handleCopy(type) {
            if (type === 'markdown') {
                this.copyToClipboard(this.noteobj?.note_text || '', 'Markdown');
            } else if (type === 'pdf') {
                this.downloadAsPDF();
            }
            this.showMoreActions = false;
        },
        handleDelete() {
            this.deleteDialogVisible = true;
            this.showMoreActions = false;
        },
        handleShare() {
            const shareurl = window.location.origin + '/share?t=' + this.noteobj.notetoken;
            this.shareUrl = shareurl;
            this.shareDialogVisible = true;
            this.showMoreActions = false;
        },
        copyShareLink() {
            if (this.$refs.shareInput) {
                this.$refs.shareInput.select();

                if (navigator.clipboard && window.isSecureContext) {
                    navigator.clipboard.writeText(this.shareUrl).then(() => {
                        this.showCenterMessage('Share link copied', 'success');
                    }).catch(() => {
                        this.showCenterMessage('Copy failed', 'error');
                    });
                } else {
                    try {
                        document.execCommand('copy');
                        this.showCenterMessage('Share link copied', 'success');
                    } catch (err) {
                        this.showCenterMessage('Copy failed', 'error');
                        console.error('Copy failed:', err);
                    }
                }
            }
        },
        closeShareDialog() {
            this.shareDialogVisible = false;
        },
        // 处理点击菜单外部的事件
        handleClickOutside(event) {
            // 如果菜单已经显示，并且点击的不是菜单或菜单按钮
            if (this.showMoreActions) {
                // 检查点击的元素是否在菜单或菜单按钮内
                const moreIcon = this.$el.querySelector('.more-icon');
                const actionMenu = this.$el.querySelector('.action-menu');

                if (moreIcon && actionMenu) {
                    // 如果点击的不是菜单和菜单按钮，则关闭菜单
                    if (!moreIcon.contains(event.target) && !actionMenu.contains(event.target)) {
                        this.showMoreActions = false;
                    }
                }
            }
        },
        copyToClipboard(text, type) {
            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(text).then(() => {
                    // 显示中央消息
                    this.showCenterMessage(type === 'Link' ? 'Share link copied' : `Copied as ${type}`, 'success');
                }).catch(() => {
                    this.showCenterMessage('Copy failed', 'error');
                });
            } else {
                const textArea = document.createElement('textarea');
                textArea.value = text;
                document.body.appendChild(textArea);
                textArea.select();
                try {
                    document.execCommand('copy');
                    // 显示中央消息
                    this.showCenterMessage(type === 'Link' ? 'Share link copied' : `Copied as ${type}`, 'success');
                } catch (err) {
                    this.showCenterMessage('Copy failed', 'error');
                    console.error('Copy failed:', err);
                }
                document.body.removeChild(textArea);
            }
        },
        downloadAsPDF() {
            // 检测是否为Safari浏览器
            const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
            
            if (isSafari) {
                // Safari浏览器使用优化后的方法
                this.generateSafariPDF();
            } else {
                // 其他浏览器使用原有方法
                this.printPdf('noteContent');
            }
        },

        // 添加Safari专用的PDF生成方法
        async generateSafariPDF() {
            try {
                const content = this.$refs.noteContent;
                if (!content) {
                    console.error('未找到需要下载的内容');
                    return;
                }

                // 显示加载信息
                this.showCenterMessage('正在准备PDF，请稍候...', 'success');
                
                // 克隆内容到临时容器，避免对原始DOM的影响
                const tempContainer = document.createElement('div');
                tempContainer.className = 'pdf-export-container';
                tempContainer.style.position = 'absolute';
                tempContainer.style.top = '-9999px';
                tempContainer.style.left = '-9999px';
                tempContainer.style.width = '800px'; // 固定宽度以确保一致的渲染
                tempContainer.style.background = 'white';
                tempContainer.style.padding = '40px';
                tempContainer.style.zIndex = '-1';
                tempContainer.innerHTML = content.innerHTML;
                
                // 添加到文档中以渲染
                document.body.appendChild(tempContainer);
                
                // 等待内容渲染完成
                await new Promise(resolve => setTimeout(resolve, 500));
                
                // 确保创建一个隐藏的iframe用于打印
                const printFrame = document.createElement('iframe');
                printFrame.style.position = 'absolute';
                printFrame.style.top = '-9999px';
                printFrame.style.left = '-9999px';
                printFrame.style.width = '0';
                printFrame.style.height = '0';
                document.body.appendChild(printFrame);
                
                // 设置iframe内容
                const frameDoc = printFrame.contentDocument || printFrame.contentWindow.document;
                frameDoc.open();
                frameDoc.write(`
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <title>${this.noteobj?.notename || 'Note'} - PDF Export</title>
                        <style>
                            body {
                                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                                color: #333;
                                line-height: 1.6;
                                margin: 0;
                                padding: 40px;
                                background: white;
                            }
                            h1, h2, h3, h4, h5, h6 {
                                margin-top: 1.5em;
                                margin-bottom: 0.5em;
                                page-break-after: avoid;
                                color: #333;
                            }
                            h1 {
                                font-size: 2.2em;
                                border-bottom: 2px solid rgba(0, 0, 0, 0.1);
                                padding-bottom: 0.3em;
                            }
                            h2 { font-size: 1.8em; }
                            h3 { font-size: 1.5em; }
                            p, blockquote, ul, ol, dl, table {
                                margin-bottom: 1em;
                                page-break-inside: avoid;
                            }
                            pre, code {
                                font-family: 'Courier New', monospace;
                                background: #f8f9fa;
                                border-radius: 4px;
                                page-break-inside: avoid;
                                white-space: pre-wrap;
                            }
                            pre {
                                padding: 1em;
                                margin: 1.5em 0;
                                border: 1px solid rgba(0, 0, 0, 0.05);
                            }
                            code {
                                padding: 0.2em 0.4em;
                                font-size: 0.9em;
                            }
                            img {
                                max-width: 100%;
                                height: auto;
                            }
                            table {
                                border-collapse: collapse;
                                width: 100%;
                                margin: 1.5em 0;
                            }
                            th, td {
                                border: 1px solid #ddd;
                                padding: 8px;
                            }
                            th {
                                background-color: #f5f5f5;
                            }
                            blockquote {
                                border-left: 4px solid #ddd;
                                padding-left: 1em;
                                color: #666;
                                margin-left: 0;
                                margin-right: 0;
                            }
                            hr {
                                border: none;
                                border-top: 1px solid #ddd;
                                margin: 2em 0;
                            }
                            a {
                                color: #4a7bff;
                                text-decoration: none;
                            }
                            .textbox h5 {
                                text-align: center;
                                color: #666;
                                margin: 20px 0 5px;
                            }
                            /* 确保每个部分都有合适的边距 */
                            .markdown-content > *:first-child,
                            .textbox > *:first-child {
                                margin-top: 0;
                            }
                            /* 定义A4纸页面设置 */
                            @page {
                                size: A4;
                                margin: 2cm;
                            }
                            @media print {
                                body {
                                    padding: 0;
                                }
                            }
                        </style>
                    </head>
                    <body>
                        ${tempContainer.innerHTML}
                    </body>
                    </html>
                `);
                frameDoc.close();
                
                // 等待iframe内容加载
                await new Promise(resolve => {
                    printFrame.onload = resolve;
                    setTimeout(resolve, 1000); // 超时保障
                });
                
                // 执行打印
                printFrame.contentWindow.focus();
                printFrame.contentWindow.print();
                
                // 稍待片刻后清理DOM
                setTimeout(() => {
                    document.body.removeChild(tempContainer);
                    document.body.removeChild(printFrame);
                    this.showCenterMessage('PDF导出完成', 'success');
                }, 1000);
                
            } catch (error) {
                console.error('Safari PDF生成失败:', error);
                this.showCenterMessage('PDF生成失败，请稍后重试', 'error');
            }
        },

        async printPdf(refName) {
            try {
                // 获取打印容器
                const printContainer = this.$refs[refName];
                if (!printContainer || !printContainer.innerHTML.trim()) {
                    throw new Error('No content to print');
                }

                // 创建一个隐藏的 iframe
                const iframe = document.createElement('iframe');
                iframe.style.position = 'absolute';
                iframe.style.top = '-9999px';
                iframe.style.left = '-9999px';
                document.body.appendChild(iframe);

                // 将打印容器的内容复制到 iframe 中
                const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
                iframeDocument.open();
                iframeDocument.write(`
                    <html>
                        <head>
                            <title>Note PDF Export</title>
                            <style>
                                body {
                                    font-family: Arial, sans-serif;
                                    color: #333;
                                    line-height: 1.6;
                                    background: white;
                                }
                                .print-container {
                                    padding: 20px;
                                    max-width: 800px;
                                    margin: 0 auto;
                                }
                                h1, h2, h3, h4, h5, h6 {
                                    margin-top: 1.5em;
                                    margin-bottom: 0.5em;
                                    page-break-after: avoid;
                                }
                                p, blockquote, ul, ol, dl, table {
                                    margin-bottom: 1em;
                                    page-break-inside: avoid;
                                }
                                pre, code {
                                    background: #f5f5f5;
                                    border-radius: 4px;
                                    padding: 0.2em 0.4em;
                                    font-family: 'Courier New', monospace;
                                    page-break-inside: avoid;
                                    white-space: pre-wrap;
                                }
                                pre {
                                    padding: 1em;
                                    overflow-x: auto;
                                }
                                img {
                                    max-width: 100%;
                                    height: auto;
                                }
                                table {
                                    border-collapse: collapse;
                                    width: 100%;
                                }
                                th, td {
                                    border: 1px solid #ddd;
                                    padding: 8px;
                                }
                                th {
                                    background-color: #f5f5f5;
                                }
                                blockquote {
                                    border-left: 4px solid #ddd;
                                    padding-left: 1em;
                                    color: #666;
                                }
                                hr {
                                    border: none;
                                    border-top: 1px solid #ddd;
                                    margin: 2em 0;
                                }
                                ${this.getAdditionalPrintStyles()}
                            </style>
                        </head>
                        <body>
                            <div class="print-container">
                                ${printContainer.innerHTML}
                            </div>
                        </body>
                    </html>
                `);
                iframeDocument.close();

                // 等待 iframe 加载完成
                await new Promise((resolve) => {
                    iframe.onload = resolve;
                });

                // 等待一小段时间，确保所有内容都已渲染
                await new Promise((resolve) => setTimeout(resolve, 1000));

                // 调用浏览器的打印功能
                iframe.contentWindow.print();

                // 移除 iframe
                setTimeout(() => {
                    document.body.removeChild(iframe);
                }, 1000);
            } catch (error) {
                console.error('PDF打印失败:', error);
                this.showCenterMessage('PDF打印失败，请稍后重试', 'error');
            }
        },

        getAdditionalPrintStyles() {
            return `
                /* Markdown 内容样式 */
                .markdown-content {
                    line-height: 1.8;
                }
                .markdown-content h1 {
                    font-size: 2.2em;
                    font-weight: 600;
                    margin: 1em 0 0.8em;
                    color: #222;
                    border-bottom: 2px solid rgba(0, 0, 0, 0.1);
                    padding-bottom: 0.3em;
                }
                .markdown-content h2 {
                    font-size: 1.8em;
                    font-weight: 600;
                    margin: 1em 0 0.8em;
                    color: #222;
                }
                .markdown-content h3 {
                    font-size: 1.5em;
                    font-weight: 500;
                    margin: 1em 0 0.7em;
                    color: #333;
                }
                .markdown-content pre {
                    background: #f8f9fa;
                    padding: 1.2em;
                    border-radius: 8px;
                    margin: 1.5em 0;
                    border: 1px solid rgba(0, 0, 0, 0.1);
                }
                .markdown-content code {
                    font-family: 'Courier New', monospace;
                    background: rgba(0, 0, 0, 0.05);
                    padding: 0.2em 0.4em;
                    border-radius: 4px;
                    font-size: 0.9em;
                    color: #333;
                }
                /* 录音笔记样式 */
                .textbox h5 {
                    text-align: center;
                    color: #666;
                    margin: 20px 0 5px;
                }
                .textbox .yuantext {
                    margin-bottom: 15px;
                }
            `;
        },
        updateNoteObj(newNoteObj) {
            this.$emit('update:noteobj', newNoteObj);
        },
        initResizer() {
            const resizer = this.$refs.resizer;
            const leftPanel = this.$refs.leftPanel;
            const rightPanel = this.$refs.rightPanel;
            const container = this.$el;

            // 设置初始宽度
            this.adjustPanelWidths();

            let x = 0;
            let leftWidth = 0;
            let rightWidth = 0;

            const mouseDownHandler = (e) => {
                x = e.clientX;
                leftWidth = leftPanel.getBoundingClientRect().width;
                rightWidth = rightPanel.getBoundingClientRect().width;

                document.addEventListener('mousemove', mouseMoveHandler);
                document.addEventListener('mouseup', mouseUpHandler);

                resizer.style.cursor = 'col-resize';
                document.body.style.cursor = 'col-resize';

                // 防止选中文本
                document.body.style.userSelect = 'none';
            };

            const mouseMoveHandler = (e) => {
                const dx = e.clientX - x;
                const containerWidth = container.clientWidth;

                let newLeftWidth = leftWidth + dx;
                let newRightWidth = rightWidth - dx;

                // 限制最小宽度，使用与CSS一致的值
                if (newLeftWidth < 420) {
                    newLeftWidth = 420;
                    newRightWidth = containerWidth - newLeftWidth - 4; // 4px为分隔条宽度
                }
                if (newRightWidth < 410) {
                    newRightWidth = 410;
                    newLeftWidth = containerWidth - newRightWidth - 4; // 4px为分隔条宽度
                }

                // 检查总宽度是否超过容器宽度
                if (newLeftWidth + newRightWidth + 4 > containerWidth) {
                    return;
                }

                leftPanel.style.width = `${newLeftWidth}px`;
                rightPanel.style.width = `${newRightWidth}px`;

                // 检查标签是否溢出以及是否需要启用紧凑模式
                this.$nextTick(() => {
                    this.checkTabsOverflow();
                    this.checkTabsCompactMode();
                });
            };

            const mouseUpHandler = () => {
                document.removeEventListener('mousemove', mouseMoveHandler);
                document.removeEventListener('mouseup', mouseUpHandler);

                resizer.style.cursor = '';
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
            };

            resizer.addEventListener('mousedown', mouseDownHandler);
        },

        // 添加调整面板宽度的方法
        adjustPanelWidths() {
            const leftPanel = this.$refs.leftPanel;
            const rightPanel = this.$refs.rightPanel;
            const container = this.$el;
            
            if (!leftPanel || !rightPanel || !container) return;

            // 获取侧边栏的当前宽度
            const sidebar = document.querySelector('aside');
            const sidebarWidth = sidebar ? sidebar.offsetWidth : 0;
            
            // 考虑侧边栏宽度的变化，调整容器的可用宽度
            const containerWidth = container.clientWidth;
            
            // 根据侧边栏的宽度调整比例
            let leftRatio = 5 / 10; // 修改默认比例为5:5
            
            // 如果侧边栏折叠了，可以给左侧面板更多空间
            if (sidebarWidth <= 70) { // 折叠状态下的宽度
                leftRatio = 5 / 10; // 保持5:5比例
            }
            
            const leftInitialWidth = (containerWidth * leftRatio);
            const rightInitialWidth = (containerWidth * (1 - leftRatio));

            // 确保宽度不小于最小值
            const leftWidth = Math.max(leftInitialWidth, 420);
            const rightWidth = Math.max(rightInitialWidth, 410);
            
            // 确保总宽度不超过容器宽度
            if (leftWidth + rightWidth <= containerWidth - 4) { // 4px为分隔条宽度
                leftPanel.style.width = `${leftWidth}px`;
                rightPanel.style.width = `${rightWidth}px`;
            } else {
                // 如果超出，按比例分配
                const totalMinWidth = 420 + 410 + 4; // 最小左宽度 + 最小右宽度 + 分隔条宽度
                if (containerWidth > totalMinWidth) {
                    const leftAdjustedWidth = Math.max(420, (containerWidth - 4) * leftRatio);
                    const rightAdjustedWidth = containerWidth - 4 - leftAdjustedWidth;
                    
                    leftPanel.style.width = `${leftAdjustedWidth}px`;
                    rightPanel.style.width = `${rightAdjustedWidth}px`;
                } else {
                    // 容器宽度太小，使用最小宽度
                    leftPanel.style.width = '420px';
                    rightPanel.style.width = '410px';
                }
            }
            
            // 更新标签溢出状态和滑块位置
            this.$nextTick(() => {
                this.checkTabsOverflow();
                this.checkTabsCompactMode(); // 检查是否需要切换到紧凑模式
                this.updateSliderPosition();
            });
        },

        checkTabsOverflow() {
            const tabsContainer = this.$refs.tabsContainer;
            const featureTabs = this.$refs.featureTabs;

            if (tabsContainer && featureTabs) {
                const isOverflowing = tabsContainer.scrollWidth > featureTabs.clientWidth;
                featureTabs.classList.toggle('has-overflow', isOverflowing);

                // 当标签溢出状态变化时更新滑块位置和宽度
                this.updateSliderPosition();
            }
        },
        updateNoteAreaHeight() {
            if (this.showMediaPlayer) {
                // 根据不同的文件类型设置默认高度
                let defaultHeight = 70; // 默认音频播放器高度
                if (this.noteobj?.filetype === 'youtube') {
                    defaultHeight = 315; // YouTube播放器默认高度
                }
                
                // 尝试获取实际高度，如果获取失败则使用默认高度
                const mediaPlayerArea = this.$el.querySelector('.media-player-area');
                let mediaPlayerHeight = mediaPlayerArea?.offsetHeight;
                
                // 如果获取到的高度为0或undefined，使用默认高度
                if (!mediaPlayerHeight || mediaPlayerHeight === 0) {
                    mediaPlayerHeight = defaultHeight;
                }
                
                this.noteAreaHeight = `calc(100vh - 150px - ${mediaPlayerHeight}px)`;
            } else {
                this.noteAreaHeight = 'calc(100vh - 150px)';
            }
        },
        // 添加音频事件处理函数
        handleAudioEnded() {
            // 音频播放结束
            this.isAudioPlaying = false;
            this.currentAudioTime = 0;
            this.audioProgressPercentage = 0;
        },

        handleAudioError(error) {
            console.error('Audio error:', error);
            this.$message.error('Failed to load audio');
            this.isAudioPlaying = false;
            this.isAudioLoading = false;
        },

        // 确认删除
        confirmDelete() {
            this.deleteDialogVisible = false;
            this.$emit('deletenote');
        },

        // 取消删除
        cancelDelete() {
            this.deleteDialogVisible = false;
        },

        // 显示中央消息的方法
        showCenterMessage(text, type = 'success') {
            this.centerMessageText = text;
            this.centerMessageType = type;
            this.centerMessageVisible = true;

            // 2秒后自动隐藏
            setTimeout(() => {
                this.centerMessageVisible = false;
            }, 2000);
        },
        // 添加重试笔记生成的方法
        retryNoteGeneration() {
            if (this.isRetrying || !this.noteobj) {
                return;
            }
            this.isRetrying = true;
            this.$emit('tryagain');
        },
        // 返回上一页
        goBack() {
            this.$router.go(-1);
        },
        // 添加窗口大小变化处理函数
        handleWindowResize() {
            // 如果已经在调整中，不要重复调整
            if (this.isAdjusting) return;
            
            this.scheduleAdjustment();
            this.updateNoteAreaHeight();
            this.checkTabsCompactMode(); // 窗口大小变化时检查紧凑模式
        },
        // 初始化加载进度条
        initLoadingProgress() {
            // 重置进度
            this.loadingProgress = 0;
            // 清除可能存在的旧定时器
            this.clearLoadingProgressInterval();
            
            // 总时间为10分钟，大约分成100个单位，每单位约为6秒
            // const totalDuration = 10 * 60 * 1000; // 10分钟，转换为毫秒
            const baseInterval = 6000; // 基础间隔为6秒
            
            this.loadingProgressInterval = setInterval(() => {
                if (this.loadingProgress >= 95) {
                    // 到95%后停止自增，等待实际加载完成
                    this.clearLoadingProgressInterval();
                    return;
                }
                
                // 计算随机增量，进度越高增量越小，模拟实际加载速度变慢
                let increment;
                if (this.loadingProgress < 30) {
                    // 前30%进度相对快些
                    increment = Math.random() * 3 + 2; // 2-5之间的随机数
                } else if (this.loadingProgress < 60) {
                    // 30%-60%进度中等
                    increment = Math.random() * 2 + 1; // 1-3之间的随机数
                } else if (this.loadingProgress < 85) {
                    // 60%-85%进度变慢
                    increment = Math.random() * 1.5 + 0.5; // 0.5-2之间的随机数
                } else {
                    // 85%以上进度很慢
                    increment = Math.random() * 0.5 + 0.1; // 0.1-0.6之间的随机数
                }
                
                // 更新进度，确保不超过95%
                this.loadingProgress = Math.min(95, this.loadingProgress + increment);
                
                // 动态调整定时器间隔，模拟实际加载时的随机性
                const randomInterval = baseInterval + Math.random() * 4000 - 2000; // 基础间隔±2秒的随机变化
                clearInterval(this.loadingProgressInterval);
                this.loadingProgressInterval = setInterval(this.incrementLoadingProgress, randomInterval);
            }, baseInterval);
        },
        
        // 增加加载进度的方法
        incrementLoadingProgress() {
            if (this.loadingProgress >= 95) {
                this.clearLoadingProgressInterval();
                return;
            }
            
            // 计算随机增量，进度越高增量越小
            let increment;
            if (this.loadingProgress < 30) {
                increment = Math.random() * 3 + 2;
            } else if (this.loadingProgress < 60) {
                increment = Math.random() * 2 + 1;
            } else if (this.loadingProgress < 85) {
                increment = Math.random() * 1.5 + 0.5;
            } else {
                increment = Math.random() * 0.5 + 0.1;
            }
            
            // 更新进度，确保不超过95%
            this.loadingProgress = Math.min(95, this.loadingProgress + increment);
        },
        
        // 清除加载进度定时器
        clearLoadingProgressInterval() {
            if (this.loadingProgressInterval) {
                clearInterval(this.loadingProgressInterval);
                this.loadingProgressInterval = null;
            }
        },
        // 添加监听侧边栏变化的方法（保留作为备用）
        setupSidebarObserver() {
            // 尝试获取父组件中的aside元素
            const sidebar = document.querySelector('aside');
            if (!sidebar) return;
            
            // 创建MutationObserver实例
            this.sidebarObserver = new MutationObserver((mutations) => {
                // 当侧边栏的属性（如类名、样式）发生变化时，重新调整面板宽度
                mutations.forEach(mutation => {
                    if (mutation.type === 'attributes' && 
                        (mutation.attributeName === 'class' || mutation.attributeName === 'style')) {
                        // 延迟执行以等待过渡动画完成
                        this.scheduleAdjustment();
                    }
                });
            });
            
            // 配置观察选项
            const config = { attributes: true, attributeFilter: ['class', 'style'] };
            
            // 开始观察
            this.sidebarObserver.observe(sidebar, config);
        },
        // 使用ResizeObserver替代MutationObserver
        setupResizeObserver() {
            // 尝试获取父组件中的aside元素
            const sidebar = document.querySelector('aside');
            if (!sidebar) return;
            
            // 保存初始宽度
            this.lastSidebarWidth = sidebar.offsetWidth;
            
            // 创建ResizeObserver实例
            this.resizeObserver = new ResizeObserver(entries => {
                for (const entry of entries) {
                    const newWidth = entry.contentRect.width;
                    
                    // 只有当宽度变化超过一定阈值时才触发调整
                    // 这有助于避免微小变化导致的频繁调整
                    if (Math.abs(newWidth - this.lastSidebarWidth) > 5) {
                        this.lastSidebarWidth = newWidth;
                        this.scheduleAdjustment();
                    }
                }
            });
            
            // 开始观察
            this.resizeObserver.observe(sidebar);
        },
        
        // 调度调整，使用防抖技术避免频繁调整
        scheduleAdjustment() {
            if (this.adjustTimeout) {
                clearTimeout(this.adjustTimeout);
            }
            
            // 标记正在调整中
            this.isAdjusting = true;
            
            // 使用渐进式调整：立即进行一次快速调整，然后在动画完成后进行精确调整
            this.quickAdjustPanelWidths();
            
            this.adjustTimeout = setTimeout(() => {
                this.adjustPanelWidths();
                this.checkTabsOverflow();
                this.checkTabsCompactMode(); // 添加对紧凑模式的检查
                this.updateSliderPosition();
                this.isAdjusting = false;
            }, 300); // 等待过渡动画完成
        },
        
        // 快速调整，立即响应但不那么精确
        quickAdjustPanelWidths() {
            const leftPanel = this.$refs.leftPanel;
            const rightPanel = this.$refs.rightPanel;
            const container = this.$el;
            
            if (!leftPanel || !rightPanel || !container) return;
            
            // 获取侧边栏的当前宽度
            const sidebar = document.querySelector('aside');
            const sidebarWidth = sidebar ? sidebar.offsetWidth : 0;
            
            // 根据侧边栏的宽度调整比例
            let leftRatio = 5 / 10; // 修改默认比例为5:5
            
            // 如果侧边栏折叠了，可以给左侧面板更多空间
            if (sidebarWidth <= 70) { // 折叠状态下的宽度
                leftRatio = 5 / 10; // 保持5:5比例
            }
            
            const containerWidth = container.clientWidth;
            const leftWidth = Math.max((containerWidth * leftRatio), 420);
            const rightWidth = Math.max((containerWidth - leftWidth - 4), 410);
            
            // 应用新的宽度
            leftPanel.style.width = `${leftWidth}px`;
            rightPanel.style.width = `${rightWidth}px`;
        },
        
        // 添加检查是否需要切换到紧凑模式的方法
        checkTabsCompactMode() {
            const rightPanel = this.$refs.rightPanel;
            if (!rightPanel) return;
            
            // 获取右侧面板宽度
            const rightPanelWidth = rightPanel.clientWidth;
            
            // 根据宽度决定是否启用紧凑模式
            this.isCompactMode = rightPanelWidth < this.compactModeThreshold;
            
            // 切换模式后更新滑块位置
            this.$nextTick(() => {
                this.updateSliderPosition();
            });
        },
        
        // 添加复制transcript的方法
        copyTranscript() {
            let transcriptText = '';
            
            // 根据笔记类型处理不同的transcript格式
            if (this.noteobj?.filetype === 'record') {
                // 录音笔记的transcript处理
                if (this.transcript?.value?.text_objs) {
                    // 修改：只复制文本内容，不包含时间标签
                    transcriptText = this.transcript.value.text_objs.map(textobj => {
                        return this.stripHtml(textobj.text);
                    }).join('\n\n');
                }
            } else {
                // 其他类型笔记的transcript处理
                if (this.transcript?.value) {
                    transcriptText = this.transcript.value;
                    
                    // 对于audio类型笔记，需要额外处理去除时间戳和序号
                    if (this.noteobj?.filetype === 'audio') {
                        // 使用更健壮的方法处理字幕格式
                        // 将文本分成行
                        const lines = transcriptText.split('\n');
                        const cleanedLines = [];
                        
                        // 跳过模式：是否跳过当前行
                        let skipNextLine = false;
                        
                        for (let i = 0; i < lines.length; i++) {
                            const line = lines[i];
                            
                            // 检查是否是数字序号行
                            const isNumberLine = /^\s*\d+\s*$/.test(line);
                            
                            // 检查是否是时间戳行
                            const isTimestampLine = /\d{2}:\d{2}[,.]\d{3}\s*-->\s*\d{2}:\d{2}/.test(line);
                            
                            // 如果当前行是数字且下一行是时间戳，则跳过这两行
                            if (isNumberLine && i + 1 < lines.length && /\d{2}:\d{2}[,.]\d{3}\s*-->\s*\d{2}:\d{2}/.test(lines[i + 1])) {
                                skipNextLine = true;
                                continue;
                            }
                            
                            // 如果需要跳过当前行（因为它是时间戳行）
                            if (skipNextLine) {
                                skipNextLine = false;
                                continue;
                            }
                            
                            // 如果当前行自身是时间戳行，跳过
                            if (isTimestampLine) {
                                continue;
                            }
                            
                            // 移除行内可能存在的其他格式的时间戳
                            let cleanedLine = line.replace(/\[\d{2}:\d{2}(:\d{2})?\]/g, '');
                            
                            // 移除行首可能存在的序号格式
                            cleanedLine = cleanedLine.replace(/^\s*(\d+[.:]|\d+\)|\s*Speaker\s+\d+:)\s*/g, '');
                            
                            // 如果清理后的行不为空，添加到结果中
                            if (cleanedLine.trim()) {
                                cleanedLines.push(cleanedLine);
                            }
                        }
                        
                        // 合并清理后的行
                        transcriptText = cleanedLines.join('\n');
                        
                        // 清理可能产生的多余空行
                        transcriptText = transcriptText.replace(/\n{3,}/g, '\n\n').trim();
                    }
                }
            }
            
            // 复制到剪贴板
            if (transcriptText) {
                this.copyToClipboard(transcriptText, 'Transcript');
            } else {
                this.showCenterMessage('No transcript content available', 'error');
            }
        },
        
        // 辅助方法：去除HTML标签
        stripHtml(html) {
            if (!html) return '';
            const doc = new DOMParser().parseFromString(html, 'text/html');
            return doc.body.textContent || '';
        },
    },

};
</script>

<style lang="less" scoped>
.new-note-container {
    display: flex;
    height: 100%;
    color: #333;
    position: relative;
    width: 100%;
    margin: 0 2vw;

    background: #f8f9fa;

    .left-panel {
        min-width: 420px;
        display: flex;
        flex-direction: column;
        border-right: none;
        transition: width 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); // 添加平滑过渡效果

        .media-player-area {
            background: #f0f2f5;
            border-radius: 15px;
            margin-bottom: 10px;
            overflow: hidden;
            margin-right: 20px;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);

            .media-player-container {
                display: flex;
                align-items: center;
                justify-content: center;

                .audio-player {
                    width: 100%;
                    height: 70px;
                    position: relative;
                    border-radius: 16px;
                    overflow: hidden;
                    background-color: #ffffff;
                    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
                    border: 1px solid rgba(0, 0, 0, 0.04);

                    .player-backdrop {
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background: linear-gradient(135deg, rgba(52, 152, 219, 0.05) 0%, rgba(52, 152, 219, 0.1) 100%);
                        z-index: 1;

                        &:before {
                            content: '';
                            position: absolute;
                            top: -50%;
                            left: -50%;
                            width: 200%;
                            height: 200%;
                            background: radial-gradient(circle, rgba(52, 152, 219, 0.08) 0%, transparent 60%);
                            animation: rotate 20s linear infinite;
                        }
                    }

                    .player-content {
                        position: relative;
                        z-index: 2;
                        display: flex;
                        align-items: center;
                        height: 100%;
                        padding: 0 20px;
                    }

                    .player-left {
                        width: 60px;
                        display: flex;
                        justify-content: center;

                        .play-button-wrapper {
                            position: relative;
                            width: 44px;
                            height: 44px;

                            .play-button {
                                width: 44px;
                                height: 44px;
                                border-radius: 50%;
                                background: #3498db;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                cursor: pointer;
                                position: relative;
                                z-index: 2;
                                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                                box-shadow: 0 4px 12px rgba(52, 152, 219, 0.3);
                                overflow: hidden;

                                .button-glow {
                                    position: absolute;
                                    top: -20%;
                                    left: -20%;
                                    right: -20%;
                                    bottom: -20%;
                                    background: radial-gradient(ellipse at center, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0) 70%);
                                    opacity: 0;
                                    transition: opacity 0.3s ease;
                                }

                                i {
                                    font-size: 22px;
                                    color: white;
                                    position: relative;
                                    transition: transform 0.2s ease;
                                }

                                &:hover {
                                    transform: scale(1.05);
                                    background-color: #2980b9;
                                }

                                &.playing {
                                    background-color: #3498db;

                                    .button-glow {
                                        opacity: 1;
                                        animation: pulse 2s infinite;
                                    }

                                    i {
                                        left: 0;
                                    }
                                }

                                &.loading {
                                    background-color: #95a5a6;
                                    cursor: not-allowed;

                                    .button-glow {
                                        display: none;
                                    }

                                    i.el-icon-loading {
                                        animation: rotate 1.2s linear infinite;
                                    }
                                }
                            }

                            .loading-text {
                                position: absolute;
                                bottom: -20px;
                                left: 50%;
                                transform: translateX(-50%);
                                white-space: nowrap;
                                font-size: 12px;
                                color: #7f8c8d;
                            }
                        }
                    }

                    .player-center {
                        flex: 1;
                        margin: 0 20px;
                        height: 40px;
                        display: flex;
                        align-items: center;

                        .loading-progress-bar {
                            width: 100%;
                            height: 6px;
                            background-color: rgba(0, 0, 0, 0.05);
                            border-radius: 3px;
                            overflow: hidden;

                            .progress-fill {
                                height: 100%;
                                background-color: #3498db;
                                border-radius: 3px;
                                transition: width 0.5s ease;
                            }
                        }

                        .audio-visualizer {
                            width: 100%;
                            height: 40px;
                            display: flex;
                            align-items: center;
                            justify-content: space-between;

                            .fill {
                                width: 15%;
                            }

                            .bar {
                                width: 4px;
                                background-color: #3498db;
                                border-radius: 2px;
                                height: 10px;
                                animation: sound-wave 0.5s infinite alternate;

                                &:nth-child(2n) {
                                    animation-delay: 0.2s;
                                    height: 15px;
                                }

                                &:nth-child(3n) {
                                    animation-delay: 0.1s;
                                    height: 20px;
                                }

                                &:nth-child(4n) {
                                    animation-delay: 0.3s;
                                    height: 12px;
                                }

                                &:nth-child(5n) {
                                    animation-delay: 0.4s;
                                    height: 18px;
                                }

                                &:nth-child(6n) {
                                    animation-delay: 0.5s;
                                    height: 14px;
                                }
                            }
                        }

                        .audio-visualizer-placeholder {
                            width: 100%;
                            height: 40px;
                            display: flex;
                            align-items: center;

                            .placeholder-line {
                                width: 100%;
                                height: 2px;
                                background-color: rgba(0, 0, 0, 0.08);
                                border-radius: 1px;
                            }
                        }
                    }

                    .player-right {
                        display: flex;
                        align-items: center;

                        .time-display {
                            margin-right: 16px;
                            font-size: 14px;
                            color: #2c3e50;
                            font-weight: 500;

                            .current-time {
                                padding: 4px 8px;
                                background-color: rgba(52, 152, 219, 0.1);
                                border-radius: 4px;
                            }
                        }

                        .audio-control-button {
                            width: 32px;
                            height: 32px;
                            border-radius: 50%;
                            background-color: rgba(52, 152, 219, 0.1);
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            color: #3498db;
                            cursor: pointer;
                            transition: all 0.2s ease;

                            &:hover {
                                background-color: rgba(52, 152, 219, 0.2);
                                transform: translateY(-2px);
                            }

                            i {
                                font-size: 16px;
                            }
                        }
                    }
                }
                .youtube-player{
                    background: #000;
                    display: flex;
                    justify-content: center;
                }
            }
        }

        .note-content {
            flex: 1;
            position: relative;
            overflow: hidden;

            .copy-icons {
                position: absolute;
                top: 5px;
                right: 10px;
                display: flex;
                gap: 8px;
                z-index: 10;

                .toggle-media-icon {
                    font-size: 20px;
                    padding: 4px;
                    border-radius: 6px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    background: rgba(74, 123, 255, 0.2);
                    color: #4a7bff;

                    &.rotated {
                        transform: rotate(180deg);
                    }

                    &:hover {
                        background: rgba(74, 123, 255, 0.3);
                    }
                }

                .more-actions-container {
                    position: relative;

                    .more-icon {
                        font-size: 20px;
                        padding: 4px;
                        border-radius: 6px;
                        cursor: pointer;
                        transition: all 0.3s ease;
                        background: rgba(74, 123, 255, 0.2);
                        color: #4a7bff;
                        margin-right: 10px;

                        &:hover {
                            background: rgba(74, 123, 255, 0.3);
                        }
                    }

                    .action-menu {
                        position: absolute;
                        top: 100%;
                        right: 10px;
                        margin-top: 8px;
                        background: #ffffff;
                        border-radius: 8px;
                        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
                        width: 190px;
                        overflow: hidden;
                        z-index: 100;

                        .action-item {
                            display: flex;
                            align-items: center;
                            padding: 10px 12px;
                            cursor: pointer;
                            transition: all 0.2s ease;
                            color: #333;

                            i {
                                font-size: 18px;
                                margin-right: 10px;
                                color: #4a7bff;
                            }

                            span {
                                font-size: 14px;
                            }

                            &:hover {
                                background: rgba(74, 123, 255, 0.1);
                            }

                            &.delete-item {
                                color: #ff4d4f;

                                i {
                                    color: #ff4d4f;
                                }

                                &:hover {
                                    background: rgba(255, 77, 79, 0.1);
                                }
                            }
                        }
                    }
                }
            }

            /* 骨架屏样式 */
            .note-skeleton {
                height: calc(100vh - 150px);
                overflow-y: auto;
                padding: 15px 0px 24px;

                .note-loading-progress {
                    position: absolute;
                    top: 10px;
                    left: 15px;
                    width: calc(100% - 120px);
                    display: flex;
                    align-items: center;
                    
                    // background-color: rgba(255, 255, 255, 0.95);
                    z-index: 10;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                    border-radius: 6px;
                    margin-bottom: 15px;

                    .progress-track {
                        flex: 1;
                        height: 6px;
                        background-color: rgba(0, 0, 0, 0.06);
                        border-radius: 3px;
                        overflow: hidden;
                        margin-right: 15px;

                        .progress-fill {
                            height: 100%;
                            background-color: #4a7bff;
                            width: 0;
                            transition: width 0.5s ease;
                            border-radius: 3px;
                            position: relative;
                            
                            &::after {
                                content: '';
                                position: absolute;
                                top: 0;
                                right: 0;
                                bottom: 0;
                                width: 100px;
                                background: linear-gradient(90deg, rgba(74, 123, 255, 0) 0%, rgba(255, 255, 255, 0.3) 50%, rgba(74, 123, 255, 0) 100%);
                                animation: shimmer 2s infinite;
                            }
                        }
                    }

                    .progress-text {
                        min-width: 45px;
                        text-align: right;
                        font-size: 13px;
                        font-weight: 500;
                        color: #4a7bff;
                    }
                }
                
                .skeleton-header {
                    margin-bottom: 30px;
                    padding: 0 15px;

                    .skeleton-title {
                        height: 36px;
                        width: 70%;
                        background-color: rgba(0, 0, 0, 0.05);
                        border-radius: 6px;
                        margin-bottom: 12px;
                        animation: pulse 1.5s infinite;
                    }

                    .skeleton-subtitle {
                        height: 20px;
                        width: 40%;
                        background-color: rgba(0, 0, 0, 0.05);
                        border-radius: 4px;
                        animation: pulse 1.5s infinite;
                    }
                }

                .skeleton-content {
                    padding: 0 15px;
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                    max-width: 900px;
                    margin: 0 auto;

                    .skeleton-paragraph {
                        height: 18px;
                        width: 100%;
                        background-color: rgba(0, 0, 0, 0.05);
                        border-radius: 4px;
                        animation: pulse 1.5s infinite;

                        &:nth-child(3n) {
                            width: 92%;
                        }

                        &:nth-child(3n+1) {
                            width: 88%;
                        }
                    }

                    .skeleton-heading {
                        height: 28px;
                        width: 60%;
                        background-color: rgba(0, 0, 0, 0.05);
                        border-radius: 6px;
                        margin-top: 10px;
                        animation: pulse 1.5s infinite;
                    }

                    .skeleton-code-block {
                        height: 120px;
                        width: 100%;
                        background-color: rgba(0, 0, 0, 0.02);
                        border-radius: 8px;
                        border: 1px solid rgba(0, 0, 0, 0.05);
                        margin: 10px 0;
                        animation: pulse 1.5s infinite;
                    }

                    .skeleton-list {
                        display: flex;
                        flex-direction: column;
                        gap: 10px;
                        padding-left: 20px;
                        margin: 5px 0;

                        .skeleton-list-item {
                            height: 16px;
                            width: 90%;
                            background-color: rgba(0, 0, 0, 0.05);
                            border-radius: 4px;
                            animation: pulse 1.5s infinite;

                            &:nth-child(1) {
                                width: 85%;
                            }

                            &:nth-child(2) {
                                width: 92%;
                            }
                        }
                    }
                }
            }

            .note-render-area {
                height: v-bind(noteAreaHeight);
                overflow-y: auto;
                padding: 15px 15px 24px;
                transition: height 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                background-color: #fcfcfc;
                border-radius: 12px;
                box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.03);
                
                .textbox {
                    h5 {
                        text-align: center;
                        color: #666;
                    }
                }
            }
        }
    }

    .resizer {
        width: 4px;
        cursor: col-resize;
        background: rgba(0, 0, 0, 0.05);
        transition: background 0.3s ease;
        position: relative;
        
        /* 添加拖拽提示效果 */
        &::before {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 6px;
            height: 40px;
            background: rgba(74, 123, 255, 0.3);
            border-radius: 3px;
            transition: all 0.3s ease;
        }
        
        /* 添加拖拽图标 */
        &::after {
            content: '⋮⋮';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            color: #fff;
            font-size: 16px;
            line-height: 40px;
            pointer-events: none;
        }

        &:hover,
        &:active {
            background: rgba(0, 0, 0, 0.1);
            
            &::before {
                background: rgba(74, 123, 255, 0.6);
                height: 80px;
            }
        }
    }

    .right-panel {
        min-width: 410px;
        display: flex;
        flex-direction: column;
        background: #fff;
        box-shadow: -2px 0 10px rgba(0, 0, 0, 0.03);
        transition: width 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); // 添加平滑过渡效果

        .feature-tabs {
            position: relative;
            padding: 0;
            border-bottom: 1px solid rgba(0, 0, 0, 0.08);
            overflow: hidden;
            background: #f8f9fa;

            .tabs-container {
                display: flex;
                flex-wrap: nowrap; // 禁止标签换行
                overflow-x: auto; // 允许水平滚动但隐藏滚动条
                scrollbar-width: none; // Firefox
                -ms-overflow-style: none; // IE/Edge
                position: relative;
                padding-left: 1px;

                &::-webkit-scrollbar {
                    display: none; // Chrome/Safari/Opera
                }

                .slider {
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    height: 3px;
                    background-color: #4a7bff;
                    transition: transform 0.3s ease, width 0.3s ease;
                }
            }

            .tabs-fade-overlay {
                display: none;
                position: absolute;
                top: 0;
                right: 0;
                height: 100%;
                width: 60px;
                background: linear-gradient(90deg, rgba(248, 249, 250, 0) 0%, #f8f9fa 100%);
                pointer-events: none; // 允许点击穿透
                border-radius: 0 8px 8px 0;
            }

            &.has-overflow .tabs-fade-overlay {
                display: block;
            }

            .tab-item {
                text-align: center;
                flex: 1;
                padding: 10px 0;
                cursor: pointer;
                transition: all 0.3s ease;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 4px;
                font-size: 13px;
                background: transparent;
                border-radius: 0;
                border-radius: 8px 8px 0 0;
                min-width: 90px;
                color: #666;

                .tab-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    height: 18px;
                    width: 18px;
                }

                svg {
                    width: 16px;
                    height: 16px;
                    stroke: currentColor;
                    stroke-width: 2;
                    stroke-linecap: round;
                    stroke-linejoin: round;
                    fill: none;
                }

                &:hover {
                    background: rgba(0, 0, 0, 0.02);
                    color: #4a7bff;

                    svg {
                        stroke: #4a7bff;
                    }
                }

                &.active {
                    color: #4a7bff;
                    background: rgba(74, 123, 255, 0.08);
                    font-weight: 500;

                    svg {
                        stroke: #4a7bff;
                    }
                }
                
                // 紧凑模式样式
                &.icon-only {
                    min-width: 40px;
                    width: 40px;
                    padding: 10px 0;
                    position: relative;
                    overflow: hidden;
                    
                    .tab-icon {
                        width: 22px;
                        height: 22px;
                        
                        svg {
                            width: 18px;
                            height: 18px;
                            transition: all 0.2s ease;
                        }
                    }
                    
                    // 紧凑模式下悬浮效果增强
                    &:hover {
                        .tab-icon {
                            transform: scale(1.1);
                            
                            svg {
                                stroke-width: 2.2;
                            }
                        }
                    }
                    
                    // 紧凑模式下活跃标签效果增强
                    &.active {
                        .tab-icon {
                            svg {
                                stroke-width: 2.3;
                            }
                        }
                        
                        // 添加底部指示线
                        &::after {
                            content: '';
                            position: absolute;
                            bottom: 0;
                            left: 50%;
                            transform: translateX(-50%);
                            width: 20px;
                            height: 3px;
                            background: #4a7bff;
                            border-radius: 3px 3px 0 0;
                            box-shadow: 0 -1px 3px rgba(74, 123, 255, 0.3);
                        }
                    }
                }
            }

            .close-button {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 5px;
                padding: 0 15px;
                height: 40px;
                border-radius: 6px;
                background: rgba(255, 76, 76, 0.1);
                cursor: pointer;
                transition: all 0.3s ease;
                color: #ff4c4c;

                i {
                    font-size: 16px;
                    color: #ff4c4c;
                }

                &:hover {
                    background: rgba(255, 76, 76, 0.2);
                }
            }
        }

        .feature-content {
            flex: 1;
            overflow: hidden;

            .feature-component {
                padding: 0 10px;
                height: 100%;
                overflow-y: auto;
            }
        }

        // 添加transcript容器的样式
        .transcript-container {
            position: relative;
            height: 100%;
            overflow: hidden;
            
            .transcript-tools {
                position: absolute;
                top: 15px;
                right: 25px;
                z-index: 10;
                
                .transcript-copy-btn {
                    width: 36px;
                    height: 36px;
                    border-radius: 8px;
                    background: rgba(74, 123, 255, 0.1);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    color: #4a7bff;
                    
                    i {
                        font-size: 18px;
                    }
                    
                    &:hover {
                        background: rgba(74, 123, 255, 0.2);
                        transform: translateY(-2px);
                        box-shadow: 0 3px 8px rgba(74, 123, 255, 0.15);
                    }
                    
                    &:active {
                        transform: translateY(0);
                    }
                }
            }
        }
    }
}

/* 骨架屏动画 */
@keyframes pulse {
    0% {
        opacity: 0.6;
    }

    50% {
        opacity: 0.3;
    }

    100% {
        opacity: 0.6;
    }
}

@keyframes wave {
    0% {
        background-position: 0 0;
    }

    100% {
        background-position: 30px 0;
    }
}

// Markdown 内容样式
.markdown-content {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    line-height: 1.8;
    color: #333;
    max-width: 900px;
    margin: 0 auto;
    font-size: 16px;
    letter-spacing: 0.3px;

    :deep(h1) {
        font-size: 2.2em;
        font-weight: 600;
        margin: 1.2em 0 0.8em;
        color: #222;
        border-bottom: 2px solid rgba(0, 0, 0, 0.06);
        padding-bottom: 0.3em;
        position: relative;
        
        &::after {
            content: '';
            position: absolute;
            left: 0;
            bottom: -2px;
            width: 80px;
            height: 2px;
            background: linear-gradient(90deg, #4a7bff 0%, rgba(74, 123, 255, 0.3) 100%);
        }
    }

    :deep(h2) {
        font-size: 1.8em;
        font-weight: 600;
        margin: 1.2em 0 0.8em;
        color: #222;
        padding-bottom: 0.2em;
        border-bottom: 1px solid rgba(0, 0, 0, 0.03);
    }

    :deep(h3) {
        font-size: 1.5em;
        font-weight: 500;
        margin: 1.2em 0 0.7em;
        color: #333;
    }

    :deep(h4) {
        font-size: 1.3em;
        font-weight: 500;
        margin: 1.2em 0 0.6em;
        color: #444;
    }

    :deep(p) {
        margin: 1.3em 0;
        line-height: 1.8;
        color: #444;
    }

    :deep(strong) {
        color: #222;
        font-weight: 600;
    }

    :deep(em) {
        color: #555;
    }

    :deep(ul),
    :deep(ol) {
        padding-left: 1.8em;
        margin: 1.2em 0;

        li {
            margin: 0.6em 0;
            line-height: 1.6;
            color: #444;
            padding-left: 0.5em;
            
            &::marker {
                color: #4a7bff;
            }
        }
    }

    :deep(blockquote) {
        margin: 1.8em 0;
        padding: 1em 1.2em;
        border-left: 4px solid #4a7bff;
        background: #f7faff;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(74, 123, 255, 0.07);
        position: relative;
        overflow: hidden;

        &::before {
            content: '"';
            position: absolute;
            top: -10px;
            left: 10px;
            font-size: 5em;
            color: rgba(74, 123, 255, 0.1);
            font-family: serif;
            line-height: 1;
        }

        p {
            margin: 0.5em 0;
            color: #4a5568;
            position: relative;
            font-style: italic;
        }
    }

    :deep(code) {
        font-family: 'JetBrains Mono', 'Fira Code', monospace;
        background: rgba(74, 123, 255, 0.08);
        padding: 0.2em 0.4em;
        border-radius: 4px;
        font-size: 0.9em;
        color: #4a7bff;
        font-weight: 500;
        letter-spacing: -0.3px;
    }

    :deep(pre) {
        background: #f8fafd;
        padding: 1.4em;
        border-radius: 12px;
        overflow-x: auto;
        margin: 1.8em 0;
        border: 1px solid rgba(0, 0, 0, 0.03);
        box-shadow: 0 3px 15px rgba(0, 0, 0, 0.04);
        position: relative;

        &::before {
            content: attr(data-language);
            position: absolute;
            top: 0;
            right: 0;
            background: rgba(74, 123, 255, 0.1);
            padding: 0.3em 0.8em;
            font-size: 0.8em;
            color: #4a7bff;
            border-radius: 0 12px 0 6px;
            text-transform: uppercase;
            font-weight: 500;
        }

        code {
            background: none;
            padding: 0;
            font-size: 0.95em;
            color: #333;
            text-shadow: none;
            white-space: pre;
            word-spacing: normal;
            word-break: normal;
            tab-size: 4;
            hyphens: none;
            font-weight: 400;
            letter-spacing: normal;
        }
    }

    :deep(table) {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        margin: 1.5em 0;
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 2px 15px rgba(0, 0, 0, 0.05);
        background: #fff;
        font-size: 15px;
        line-height: 1.6;

        th,
        td {
            border: none;
            border-bottom: 1px solid rgba(0, 0, 0, 0.06);
            padding: 14px 18px;
            text-align: left;
            position: relative;
        }

        th {
            background: #f7faff;
            font-weight: 600;
            color: #2c3e50;
            font-size: 15px;
            text-transform: capitalize;
            border-bottom: 2px solid rgba(74, 123, 255, 0.2);
            position: sticky;
            top: 0;
            z-index: 2;
        }
        
        thead tr {
            border-bottom: 2px solid rgba(74, 123, 255, 0.15);
        }

        tbody tr {
            transition: all 0.2s ease;
            
            &:hover {
                background-color: rgba(74, 123, 255, 0.04);
            }
            
            &:last-child td {
                border-bottom: none;
            }
        }

        tr:nth-child(even) {
            background: rgba(0, 0, 0, 0.01);
        }
        
        td {
            color: #34495e;
        }
        
        /* 添加列分隔线，但不添加到最后一列 */
        td:not(:last-child)::after {
            content: '';
            position: absolute;
            right: 0;
            top: 25%;
            height: 50%;
            width: 1px;
            background: rgba(0, 0, 0, 0.04);
        }
    }

    :deep(hr) {
        border: none;
        height: 1px;
        background: rgba(0, 0, 0, 0.08);
        margin: 2em 0;
    }

    :deep(img) {
        max-width: 100%;
        border-radius: 8px;
        margin: 1.5em 0;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    :deep(a) {
        color: #4a7bff;
        text-decoration: none;
        border-bottom: 1px solid rgba(74, 123, 255, 0.3);
        transition: all 0.3s ease;

        &:hover {
            border-bottom-color: #4a7bff;
        }
    }

    // 任务列表样式
    :deep(input[type="checkbox"]) {
        margin-right: 0.5em;
        cursor: pointer;
    }

    // 代码块语言标识
    :deep(.language-label) {
        position: absolute;
        top: 0;
        right: 1em;
        background: rgba(0, 0, 0, 0.05);
        padding: 0.2em 0.6em;
        border-radius: 0 0 4px 4px;
        font-size: 0.8em;
        color: #888;
    }
}

.markdown-content-noh {
    @extend .markdown-content;

    :deep(h1),
    :deep(h2),
    :deep(h3),
    :deep(h4),
    :deep(h5),
    :deep(h6) {
        font-size: 1em;
        margin: 0.5em 0;
        color: #888;
        border: none;
        padding: 0;
    }
}

// 打印样式
@media print {
    .new-note-container {
        display: block;
        background: white;
        .right-panel,
        .feature-buttons,
        .copy-icons {
            display: none;
        }

        .left-panel {
            border: none;
        }

        .note-render-area {
            padding: 0;
        }
    }
}

// 针对特定区域的滚动条样式
.note-render-area {
    &::-webkit-scrollbar {
        width: 8px;
    }

    &::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.02);
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(74, 123, 255, 0.2);
        border-radius: 4px;
        border: 2px solid #fcfcfc;

        &:hover {
            background: rgba(74, 123, 255, 0.3);
        }
    }
}

// 虚拟滚动相关样式
.virtual-scroll-info {
    position: fixed;
    bottom: 20px;
    right: 50%;
    transform: translateX(50%);
    z-index: 100;
    display: flex;
    justify-content: center;
    pointer-events: none;
    
    .scroll-info-badge {
        background: rgba(74, 123, 255, 0.9);
        color: white;
        padding: 8px 16px;
        border-radius: 20px;
        font-size: 13px;
        box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
        display: flex;
        align-items: center;
        gap: 6px;
        
        i {
            font-size: 14px;
        }
    }
}

.scroll-placeholder {
    transition: height 0.3s ease;
    
    &.top-placeholder {
        margin-bottom: 10px;
    }
    
    &.bottom-placeholder {
        margin-top: 10px;
    }
}

.feature-content {
    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.02);
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.1);

        &:hover {
            background: rgba(0, 0, 0, 0.2);
        }
    }
}

.feature-btn,
.tab-item {

    img {
        width: 16px;
        height: 16px;
        vertical-align: middle;
        display: inline-block;
    }
}

// 添加动画效果
.action-menu-enter-active,
.action-menu-leave-active {
    transition: opacity 0.3s, transform 0.3s;
    transform-origin: top right;
}

.action-menu-enter-from,
.action-menu-leave-to {
    opacity: 0;
    transform: scale(0.8);
}

// 添加媒体播放器过渡动画
.media-player-enter-active,
.media-player-leave-active {
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    max-height: 400px;
    opacity: 1;
    transform-origin: top;
}

.media-player-enter-from,
.media-player-leave-to {
    max-height: 0;
    opacity: 0;
    padding: 0;
    margin: 0;
    transform: translateY(-10px);
}

// 内容淡入淡出效果
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: translateY(10px);
}

@keyframes rotate {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

@keyframes sound-wave {
    0% {
        transform: scaleY(0.3);
    }

    50% {
        transform: scaleY(1);
    }

    100% {
        transform: scaleY(0.3);
    }
}

@keyframes fadeInOut {
    0% {
        opacity: 0.5;
    }

    50% {
        opacity: 1;
    }

    100% {
        opacity: 0.5;
    }
}

// 添加删除确认对话框样式
:deep(.delete-confirm-dialog) {
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 6px 30px rgba(0, 0, 0, 0.1);

    .el-dialog__header {
        padding: 15px 20px;
        background: #f9f9f9;
        border-bottom: 1px solid #eee;

        .el-dialog__title {
            font-size: 18px;
            font-weight: 600;
            color: #333;
        }
    }

    .el-dialog__body {
        padding: 25px 20px;
    }

    .el-dialog__footer {
        padding: 12px 20px;
        border-top: 1px solid #eee;
    }
}

.delete-dialog-content {
    display: flex;
    align-items: flex-start;

    .warning-icon {
        font-size: 28px;
        color: #ff4d4f;
        margin-right: 16px;
        padding-top: 3px;
    }

    .delete-message {
        .delete-title {
            font-size: 16px;
            font-weight: 500;
            color: #333;
            margin: 0 0 8px 0;
        }

        .delete-desc {
            font-size: 14px;
            color: #666;
            margin: 0;
            line-height: 1.5;
        }
    }
}

.dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;

    .el-button {
        padding: 9px 20px;
        font-size: 14px;
        border-radius: 6px;
        transition: all 0.3s;

        &.el-button--danger {
            background: #ff4d4f;
            border-color: #ff4d4f;

            &:hover {
                background: #ff3236;
                border-color: #ff3236;
            }
        }
    }
}

// 添加中央消息样式
.center-message {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 12px 24px;
    border-radius: 8px;
    z-index: 9999;
    display: flex;
    align-items: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

    &.success {
        i {
            color: #67c23a;
        }
    }

    &.error {
        i {
            color: #f56c6c;
        }
    }

    i {
        font-size: 20px;
        margin-right: 10px;
    }

    span {
        font-size: 15px;
        font-weight: 500;
    }
}

.center-message-fade-enter-active,
.center-message-fade-leave-active {
    transition: opacity 0.3s, transform 0.3s;
}

.center-message-fade-enter-from,
.center-message-fade-leave-to {
    opacity: 0;
    transform: translate(-50%, -30%);
}

// 自定义分享弹窗样式
.custom-share-dialog-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
}

.custom-share-dialog {
    background: white;
    width: 660px;
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
    overflow: hidden;
    animation: dialog-bounce 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);

    .share-dialog-header {
        display: flex;
        justify-content: flex-end;
        padding: 12px;

        .close-button {
            background: transparent;
            border: none;
            width: 32px;
            height: 32px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.2s;

            i {
                font-size: 18px;
                color: #909399;
            }

            &:hover {
                background: rgba(0, 0, 0, 0.05);

                i {
                    color: #606266;
                }
            }
        }
    }
}

.share-dialog-content {
    padding: 0 30px 30px;
    text-align: center;

    h3 {
        font-size: 22px;
        font-weight: 600;
        color: #303133;
        margin-bottom: 12px;
    }

    p {
        font-size: 14px;
        color: #606266;
        margin-bottom: 25px;
        line-height: 1.6;
    }

    .share-link-container {
        display: flex;
        align-items: center;
        background: #f5f7fa;
        border-radius: 8px;
        border: 1px solid #e4e7ed;
        padding: 5px 5px 5px 16px;
        margin-top: 15px;
        box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.03);

        .share-input {
            flex: 1;
            height: 38px;
            border: none;
            background: transparent;
            color: #303133;
            font-size: 14px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            outline: none;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .copy-button {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            height: 38px;
            padding: 0 20px;
            background: #6c5ce7;
            color: white;
            border: none;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
            box-shadow: 0 2px 5px rgba(108, 92, 231, 0.2);

            &:hover {
                background: #5d4fd0;
                transform: translateY(-1px);
                box-shadow: 0 4px 8px rgba(108, 92, 231, 0.3);
            }

            &:active {
                transform: translateY(0);
                box-shadow: 0 2px 4px rgba(108, 92, 231, 0.2);
            }

            i {
                font-size: 16px;
            }
        }
    }
}

// 分享弹窗动画
.share-fade-enter-active {
    animation: dialog-fade-in 0.3s;

    .custom-share-dialog {
        animation: dialog-bounce 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    }
}

.share-fade-leave-active {
    animation: dialog-fade-out 0.2s;

    .custom-share-dialog {
        animation: dialog-zoom-out 0.2s ease-in-out;
    }
}

@keyframes dialog-fade-in {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

@keyframes dialog-fade-out {
    from {
        opacity: 1;
    }

    to {
        opacity: 0;
    }
}

@keyframes dialog-bounce {
    0% {
        opacity: 0;
        transform: scale(0.9);
    }

    70% {
        opacity: 1;
        transform: scale(1.03);
    }

    100% {
        opacity: 1;
        transform: scale(1);
    }
}

@keyframes dialog-zoom-out {
    from {
        opacity: 1;
        transform: scale(1);
    }

    to {
        opacity: 0;
        transform: scale(0.9);
    }
}

// 添加自定义tooltip样式
:deep(.el-tooltip__popper) {
    background: #6c5ce7 !important; // 使用优雅的紫色调
    border: none !important;
    box-shadow: 0 4px 12px rgba(108, 92, 231, 0.2) !important;
    font-size: 13px !important;
    padding: 8px 14px !important;
    color: #ffffff !important; // 白色文字
    border-radius: 6px !important;
    font-weight: 500 !important;
    line-height: 1.4 !important;

    &[x-placement^="top"] .popper__arrow {
        border-top-color: #6c5ce7 !important;

        &:after {
            border-top-color: #6c5ce7 !important;
        }
    }

    &[x-placement^="bottom"] .popper__arrow {
        border-bottom-color: #6c5ce7 !important;

        &:after {
            border-bottom-color: #6c5ce7 !important;
        }
    }

    &[x-placement^="left"] .popper__arrow {
        border-left-color: #6c5ce7 !important;

        &:after {
            border-left-color: #6c5ce7 !important;
        }
    }

    &[x-placement^="right"] .popper__arrow {
        border-right-color: #6c5ce7 !important;

        &:after {
            border-right-color: #6c5ce7 !important;
        }
    }
}

// 添加自定义tooltip样式，更高特异性选择器
.new-note-container .left-panel .note-content .copy-icons {
    :deep(.el-tooltip__popper) {
        background-color: #6c5ce7 !important;
        border: none !important;
        box-shadow: 0 4px 12px rgba(108, 92, 231, 0.2) !important;
        font-size: 13px !important;
        padding: 8px 14px !important;
        color: #ffffff !important;
        border-radius: 6px !important;
        font-weight: 500 !important;
        z-index: 9999 !important;

        &[x-placement^="top"] .popper__arrow,
        &[x-placement^="bottom"] .popper__arrow,
        &[x-placement^="left"] .popper__arrow,
        &[x-placement^="right"] .popper__arrow {
            border-color: transparent !important;
        }

        &[x-placement^="bottom"] .popper__arrow:after {
            border-bottom-color: #6c5ce7 !important;
        }

        &[x-placement^="top"] .popper__arrow:after {
            border-top-color: #6c5ce7 !important;
        }

        &[x-placement^="left"] .popper__arrow:after {
            border-left-color: #6c5ce7 !important;
        }

        &[x-placement^="right"] .popper__arrow:after {
            border-right-color: #6c5ce7 !important;
        }
    }
}



// 添加全局样式而不是deep选择器</style>

<style lang="less">
.el-tooltip__popper {
    background-color: #a6c8ff !important; // 更淡雅的蓝色
    border: none !important;
    box-shadow: 0 4px 12px rgba(166, 200, 255, 0.3) !important;
    font-size: 13px !important;
    padding: 8px 14px !important;
    color: #333333 !important; // 深色文字，确保在淡色背景上有足够对比度
    border-radius: 6px !important;
    font-weight: 500 !important;
    z-index: 9999 !important;

    &.is-dark {
        background-color: #a6c8ff !important;
    }

    .popper__arrow {
        border-color: transparent !important;
    }

    &[x-placement^="bottom"] .popper__arrow:after {
        border-bottom-color: #a6c8ff !important;
    }

    &[x-placement^="top"] .popper__arrow:after {
        border-top-color: #a6c8ff !important;
    }

    &[x-placement^="left"] .popper__arrow:after {
        border-left-color: #a6c8ff !important;
    }

    &[x-placement^="right"] .popper__arrow:after {
        border-right-color: #a6c8ff !important;
    }
}

// 为标签特定的tooltip添加样式
.tabs-container {
    .el-tooltip__popper {
        background-color: #6c5ce7 !important; // 使用更深的紫色
        color: #ffffff !important; // 白色文字
        padding: 6px 12px !important;
        font-size: 12px !important;
        font-weight: 500 !important;
        box-shadow: 0 4px 15px rgba(108, 92, 231, 0.25) !important;
        transition: all 0.3s ease !important;
        
        // 确保箭头颜色匹配
        &[x-placement^="top"] .popper__arrow:after {
            border-top-color: #6c5ce7 !important;
        }
        
        // 添加动画效果
        animation: tooltip-appear 0.2s ease-out !important;
    }
}

@keyframes tooltip-appear {
    from {
        opacity: 0;
        transform: translateY(5px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.note-failed-container {
    height: calc(100vh - 250px);
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    animation: fadeIn 0.5s ease;

    .failed-content {
        max-width: 460px;
        padding: 35px 40px;
        text-align: center;
        background: #ffffff;
        border-radius: 16px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06), 0 5px 15px rgba(0, 0, 0, 0.03);
        border: 1px solid rgba(0, 0, 0, 0.05);
        transform: translateY(0);
        transition: all 0.3s ease;

        .failed-title {
            font-size: 24px;
            font-weight: 600;
            margin-bottom: 12px;
            color: #333;
            letter-spacing: -0.3px;
        }

        .failed-message {
            font-size: 16px;
            color: #666;
            margin-bottom: 25px;
            line-height: 1.5;
            font-weight: 400;
        }

        .failed-actions {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 15px;

            .retry-button {
                padding: 12px 25px;
                font-size: 15px;
                color: #fff;
                background: #4a7bff;
                border: none;
                border-radius: 8px;
                font-weight: 500;
                cursor: pointer;
                transition: all 0.2s ease;
                box-shadow: 0 3px 10px rgba(74, 123, 255, 0.2);
                display: flex;
                align-items: center;
                justify-content: center;
                min-width: 160px;
                gap: 8px;

                &:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 5px 15px rgba(74, 123, 255, 0.3);
                    background: #3a6eef;
                }

                &:active {
                    transform: translateY(0);
                    box-shadow: 0 3px 8px rgba(74, 123, 255, 0.2);
                }

                &:disabled {
                    background: #a6b2e0;
                    cursor: not-allowed;
                    transform: none;
                    box-shadow: none;
                }

                i {
                    margin-right: 8px;
                    font-size: 18px;
                    animation: spin 1s infinite linear;
                }
            }

            .back-button {
                padding: 11px 25px;
                font-size: 15px;
                color: #666;
                background: #f5f5f5;
                border: 1px solid rgba(0, 0, 0, 0.08);
                border-radius: 8px;
                font-weight: 500;
                cursor: pointer;
                transition: all 0.2s ease;
                min-width: 160px;

                &:hover {
                    background: #eee;
                    color: #444;
                }

                &:active {
                    background: #e8e8e8;
                }
            }

            .retry-limit-message {
                font-size: 14px;
                color: #666;
                background: #f8f8f8;
                padding: 12px 16px;
                border-radius: 8px;
                max-width: 320px;
                line-height: 1.5;
                display: flex;
                align-items: center;
                justify-content: center;
                text-align: center;
                border: 1px solid rgba(0, 0, 0, 0.05);
            }
        }
    }
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(15px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

// 为失败状态禁用功能区域添加样式
.feature-disabled-container {
    height: calc(100vh - 160px);
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    animation: fadeIn 0.5s ease;

    .disabled-content {
        max-width: 460px;
        padding: 35px 40px;
        text-align: center;
        background: #ffffff;
        border-radius: 16px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06), 0 5px 15px rgba(0, 0, 0, 0.03);
        border: 1px solid rgba(0, 0, 0, 0.05);
        transform: translateY(0);
        transition: all 0.3s ease;

        i {
            font-size: 48px;
            color: #909399;
            margin-bottom: 16px;
        }

        h3 {
            font-size: 24px;
            font-weight: 600;
            margin-bottom: 12px;
            color: #333;
            letter-spacing: -0.3px;
        }

        p {
            font-size: 16px;
            color: #666;
            margin-bottom: 25px;
            line-height: 1.5;
            font-weight: 400;
        }
    }
}

// 禁用状态的标签样式
.tab-item.disabled {
    opacity: 0.6;
    cursor: not-allowed;

    &:hover {
        background: transparent;
        color: #666;

        svg {
            stroke: currentColor;
        }
    }
}

/* 添加进度条闪光动画 */
@keyframes shimmer {
    0% {
        transform: translateX(-100%);
    }
    100% {
        transform: translateX(100%);
    }
}
</style>